import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { Portal } from '../components/Portal';
import { useSettings } from '../hooks/useSettings';
import { useTenant } from '../contexts/TenantContext';

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Toggle } from "../components/ui/toggle";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Check, Globe, Settings } from 'lucide-react';

type SessionProvider = 'WAHA' | 'EVOLUTION' | 'QUEPASA' | 'EVOGO';
type VisibleSessionProvider = SessionProvider;
type SessionFilterProvider = 'ALL' | VisibleSessionProvider;

const DEFAULT_ALLOWED_PROVIDERS: VisibleSessionProvider[] = ['WAHA', 'EVOLUTION', 'QUEPASA', 'EVOGO'];

const PROVIDER_META: Record<SessionProvider, {
  label: string;
  icon: string;
  badgeClass: string;
}> = {
  WAHA: {
    label: 'Waha',
    icon: '/iconewaha.png',
    badgeClass: 'bg-green-500/10 text-green-400 border-green-500/20',
  },
  EVOLUTION: {
    label: 'Evolution API',
    icon: '/iconeevolutionapi.png',
    badgeClass: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  },
  QUEPASA: {
    label: 'Quepasa',
    icon: '/iconequepasa.png',
    badgeClass: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  },
  EVOGO: {
    label: 'Evogo',
    icon: '/iconeevogo.svg',
    badgeClass: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  },
};

// Componente para exibir contador do QR Code
function QRCountdown({ expiresAt }: { expiresAt: Date }) {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = expiresAt.getTime();
      const difference = expiry - now;
      return Math.max(0, Math.ceil(difference / 1000));
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  if (timeLeft <= 0) {
    return <span className="text-red-600 text-sm">QR Code expirado</span>;
  }

  return (
    <span className="text-orange-600 text-sm">
      Expira em {timeLeft}s
    </span>
  );
}

interface WhatsAppSession {
  name: string; // Nome real usado na API (ex: vendas_c52982e8)
  displayName?: string; // Nome exibido ao usuário (ex: vendas)
  status: 'WORKING' | 'SCAN_QR_CODE' | 'STOPPED' | 'FAILED';
  provider: SessionProvider;
  qr?: string;
  qrExpiresAt?: Date;
  me?: {
    id: string;
    pushName: string;
  };
  config?: Record<string, unknown>;
}

interface BChatConfig {
  enabled: boolean;
  inboxId?: number;
  accountId?: number | string;
  nameInbox?: string;
  reopenConversation?: boolean;
  conversationPending?: boolean;
  importContacts?: boolean;
  importMessages?: boolean;
}

interface ChatwootInboxOption {
  id: number;
  name: string;
  channel_type: string;
  api_channel?: Record<string, unknown>;
  webhook_url?: string | null;
  callback_webhook_url?: string | null;
  inbox_identifier?: string | null;
}

interface ChatwootAgentOption {
  id: number;
  account_id: number;
  name: string;
  available_name: string;
  email: string;
  role: string;
  availability_status: string;
  thumbnail: string | null;
}

interface ChatwootInboxMember {
  id: number;
  user: ChatwootAgentOption;
}

interface SettingsForm {
  rejectCall: boolean;
  msgCall: string;
  groupsIgnore: boolean;
  alwaysOnline: boolean;
  readMessages: boolean;
  syncFullHistory: boolean;
  readStatus: boolean;
}

function getProviderMeta(provider: SessionProvider) {
  return PROVIDER_META[provider];
}

function isEvogoProvider(provider?: string): provider is 'EVOGO' {
  return provider === 'EVOGO';
}

function getLocalBchatConfig(session: WhatsAppSession, accountId?: string | number | null): BChatConfig | null {
  const rawInboxId = session.config?.bchatInboxId;
  const inboxId = typeof rawInboxId === 'number' ? rawInboxId : Number(rawInboxId);
  const configuredInboxName =
    typeof session.config?.bchatInboxName === 'string' ? session.config.bchatInboxName : undefined;

  if (!Number.isFinite(inboxId) || inboxId <= 0) {
    return null;
  }

  return {
    enabled: true,
    inboxId,
    nameInbox: configuredInboxName || session.displayName || session.name,
    accountId: accountId || undefined,
    reopenConversation: session.config?.bchatReopenConversation === true,
    conversationPending: session.config?.bchatConversationPending === true,
    importContacts: session.config?.bchatImportContacts !== false,
    importMessages: session.config?.bchatImportMessages !== false,
  };
}

function normalizeWebhookPath(urlValue?: string | null): string | null {
  if (!urlValue?.trim()) {
    return null;
  }

  try {
    return new URL(urlValue).pathname.replace(/\/$/, '');
  } catch {
    return urlValue.trim().split('?')[0].replace(/\/$/, '');
  }
}

function getBackendChatwootWebhookPath(sessionName: string): string {
  return `/api/webhooks/chatwoot/${sessionName}`;
}

function getRemoteBchatConfig(
  session: WhatsAppSession,
  inboxes: ChatwootInboxOption[],
  accountId?: string | number | null
): BChatConfig | null {
  const expectedWebhookPath = getBackendChatwootWebhookPath(session.name);
  const inbox = inboxes.find((item) => {
    const webhookPaths = [
      normalizeWebhookPath(item.webhook_url),
      normalizeWebhookPath(item.callback_webhook_url),
    ];

    return webhookPaths.includes(expectedWebhookPath);
  });

  if (!inbox) {
    return null;
  }

  return {
    enabled: true,
    inboxId: inbox.id,
    nameInbox: inbox.name || session.displayName || session.name,
    accountId: accountId || undefined,
    reopenConversation: session.config?.bchatReopenConversation === true,
    conversationPending: session.config?.bchatConversationPending === true,
    importContacts: session.config?.bchatImportContacts !== false,
    importMessages: session.config?.bchatImportMessages !== false,
  };
}


export function WhatsAppConnectionsPage() {
  const { settings } = useSettings();
  const { selectedTenantId, loading: tenantLoading } = useTenant();
  const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionProvider, setNewSessionProvider] = useState<VisibleSessionProvider>('WAHA');
  const [interactiveCampaignEnabled, setInteractiveCampaignEnabled] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loadingQR, setLoadingQR] = useState<string | null>(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [currentQRSession, setCurrentQRSession] = useState<WhatsAppSession | null>(null);
  const qrRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [createSessionModalOpen, setCreateSessionModalOpen] = useState(false);
  const [providerDropdownOpen, setProviderDropdownOpen] = useState(false);
  const [allowedProviders, setAllowedProviders] = useState<VisibleSessionProvider[]>(DEFAULT_ALLOWED_PROVIDERS);
  const [filterProvider, setFilterProvider] = useState<SessionFilterProvider>('ALL');
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'WORKING' | 'SCAN_QR_CODE' | 'STOPPED' | 'FAILED'>('ALL');
  const [sortBy, setSortBy] = useState<'name' | 'status' | 'provider'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    // Adicionar tenant ID no header para SuperAdmin
    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  }, [selectedTenantId]);

  // Fetch BChat Status for a single session
  const fetchBchatStatus = useCallback(async (sessionName: string) => {
    if (!settings?.evolutionHost || !settings?.evolutionApiKey) return null;

    try {
      const cleanHost = settings.evolutionHost.replace(/\/$/, "");
      const targetUrl = `${cleanHost}/chatwoot/find/${sessionName}`;

      const response = await fetch(targetUrl, {
        method: 'GET',
        headers: {
          'apikey': settings.evolutionApiKey
        }
      });

      if (response.ok) {
        const data = await response.json();
        // If data is returned (even empty object), check if it has relevant fields to confirm it's "set"
        // Evolution usually returns the config object if enabled.
        // Adjust based on actual API response, assuming non-null/non-empty indicates success.
        if (data && data.enabled) {
          return data;
        }
      }
      return null;
    } catch (error) {
      console.warn(`Erro ao verificar BChat para ${sessionName}:`, error);
      return null;
    }
  }, [settings?.evolutionHost, settings?.evolutionApiKey]);

  const loadSessions = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }

      const response = await authenticatedFetch('/api/waha/sessions');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Processar dados das sessões incluindo QR code salvo no banco
      let processedSessions = data.map((session: WhatsAppSession) => ({
        name: session.name,
        displayName: session.displayName || session.name,
        status: session.status || 'STOPPED',
        provider: session.provider || 'WAHA',
        me: session.me || null,
        qr: session.qr || null,
        qrExpiresAt: session.qrExpiresAt ? new Date(session.qrExpiresAt) : undefined,
        config: session.config || {}
      }));
      if (filterProvider !== 'ALL') {
        processedSessions = processedSessions.filter((s: WhatsAppSession) => s.provider === filterProvider);
      }
      if (filterStatus !== 'ALL') {
        processedSessions = processedSessions.filter((s: WhatsAppSession) => s.status === filterStatus);
      }
      processedSessions = processedSessions.sort((a: WhatsAppSession, b: WhatsAppSession) => {
        let av = '';
        let bv = '';
        if (sortBy === 'name') {
          av = (a.displayName || a.name || '').toLowerCase();
          bv = (b.displayName || b.name || '').toLowerCase();
        } else if (sortBy === 'status') {
          av = (a.status || '').toLowerCase();
          bv = (b.status || '').toLowerCase();
        } else {
          av = (a.provider || '').toLowerCase();
          bv = (b.provider || '').toLowerCase();
        }
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });

      setSessions(processedSessions);
      if (showLoading) {
        setLoading(false);
      }

      let apiInboxes: ChatwootInboxOption[] = [];
      const hasChatwootConfig = Boolean(settings?.chatwootUrl && settings?.chatwootAccountId && settings?.chatwootApiToken);

      if (hasChatwootConfig && showLoading) {
        try {
          const inboxesResponse = await authenticatedFetch('/api/chatwoot/inboxes');
          if (inboxesResponse.ok) {
            const inboxPayload = await inboxesResponse.json();
            apiInboxes = Array.isArray(inboxPayload.inboxes)
              ? inboxPayload.inboxes.filter((i: ChatwootInboxOption) => i.channel_type === 'Channel::Api')
              : [];
            setAvailableInboxes(apiInboxes);
          }
        } catch (inboxesError) {
          console.warn('Erro ao carregar caixas do BChat em background:', inboxesError);
        }
      }

      if (settings?.evolutionHost && settings?.evolutionApiKey) {
        const configMap: Record<string, BChatConfig> = {};
        await Promise.all(processedSessions.map(async (s: WhatsAppSession) => {
          // Para Evolution, buscar status na API
          if (s.provider === 'EVOLUTION') {
            const config = await fetchBchatStatus(s.name);
            if (config) {
              configMap[s.name] = config;
            }
          }
          // Para integrações locais, verificar no config da sessão
          else {
            const localConfig = getLocalBchatConfig(s, settings?.chatwootAccountId)
              || getRemoteBchatConfig(s, apiInboxes, settings?.chatwootAccountId);
            if (localConfig) {
              configMap[s.name] = localConfig;
            }
          }
        }));
        setBchatConfigs(configMap);
      } else {
        // Se não tem Evolution configurado, ainda podemos mostrar status para WAHA/Quepasa
        const configMap: Record<string, BChatConfig> = {};
        processedSessions.forEach((s: WhatsAppSession) => {
          const localConfig = getLocalBchatConfig(s, settings?.chatwootAccountId)
            || getRemoteBchatConfig(s, apiInboxes, settings?.chatwootAccountId);
          if (localConfig) {
            configMap[s.name] = localConfig;
          }
        });
        setBchatConfigs(configMap);
      }
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
      if (showLoading) {
        toast.error('Erro ao carregar conexões WhatsApp');
        setLoading(false);
      }
    }
  }, [authenticatedFetch, filterProvider, filterStatus, sortBy, sortDir, settings?.evolutionHost, settings?.evolutionApiKey, settings?.chatwootUrl, settings?.chatwootAccountId, settings?.chatwootApiToken, fetchBchatStatus]);

  const openQRModal = useCallback(async function openQRModal(sessionName: string, options?: { skipStart?: boolean }) {
    try {
      if (qrRetryTimeoutRef.current) {
        clearTimeout(qrRetryTimeoutRef.current);
        qrRetryTimeoutRef.current = null;
      }

      // Primeiro, verificar se já temos um QR salvo no banco para esta sessão
      const currentSession = sessions.find(s => s.name === sessionName);

      if (currentSession?.qr && currentSession?.qrExpiresAt && currentSession.qrExpiresAt > new Date()) {
        // Usar QR code já salvo no banco se ainda não expirou
        const sessionWithQR: WhatsAppSession = {
          name: sessionName,
          status: 'SCAN_QR_CODE',
          provider: currentSession.provider,
          qr: currentSession.qr,
          qrExpiresAt: currentSession.qrExpiresAt,
          me: currentSession.me
        };

        setCurrentQRSession(sessionWithQR);
        setQrModalOpen(true);
        return;
      }

      const shouldStartSession = !options?.skipStart && (!currentSession || currentSession.status === 'STOPPED');
      if (shouldStartSession) {
        const startResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/start`, {
          method: 'POST'
        });

        if (!startResponse.ok) {
          const startError = await startResponse.json();
          toast.error(`Erro ao iniciar conexão: ${startError.error || 'Erro desconhecido'}`);
          return;
        }

        // Aguardar um pouco para a sessão inicializar
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Agora buscar QR através do backend (que vai rotear para API correta)
      const qrResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/auth/qr`);

      if (qrResponse.ok) {
        const qrData = await qrResponse.json();

        if (qrData.qr) {
          const sessionWithQR: WhatsAppSession = {
            name: sessionName,
            status: 'SCAN_QR_CODE',
            provider: currentSession?.provider || 'WAHA',
            qr: qrData.qr,
            qrExpiresAt: qrData.expiresAt ? new Date(qrData.expiresAt) : new Date(Date.now() + 60000),
            me: undefined
          };

          setCurrentQRSession(sessionWithQR);
          setQrModalOpen(true);

          // Recarregar sessões para obter dados atualizados do banco
          setTimeout(() => {
            loadSessions(false);
          }, 1000);
        } else {
          toast.error('QR Code não disponível');
        }
      } else if (qrResponse.status === 202) {
        const qrData = await qrResponse.json().catch(() => ({} as Record<string, unknown>));
        const retryAfterMs = typeof (qrData as { retryAfterMs?: unknown }).retryAfterMs === 'number'
          ? (qrData as { retryAfterMs: number }).retryAfterMs
          : 1500;
        const provider = (qrData as { provider?: unknown }).provider;
        const error = (qrData as { error?: unknown }).error;

        if (error === 'QR_NOT_READY') {
          const sessionWithQR: WhatsAppSession = {
            name: sessionName,
            displayName: currentSession?.displayName,
            status: 'SCAN_QR_CODE',
            provider: (currentSession?.provider || (typeof provider === 'string' ? provider : 'WAHA')) as SessionProvider,
            qr: undefined,
            qrExpiresAt: undefined,
            me: undefined
          };

          setCurrentQRSession(sessionWithQR);
          setQrModalOpen(true);

          qrRetryTimeoutRef.current = setTimeout(() => {
            void openQRModal(sessionName, { skipStart: true });
          }, retryAfterMs);
          return;
        }

        toast.error((qrData as { details?: string }).details || 'QR Code não disponível');
      } else {
        const errorData = await qrResponse.json().catch(() => ({}));
        toast.error(errorData.details || errorData.error || 'Erro ao buscar QR Code');
      }
    } catch (error) {
      console.error('Erro ao buscar QR Code:', error);
      toast.error('Erro ao buscar QR Code');
    }
  }, [sessions, authenticatedFetch, loadSessions]);

  // BChat Dialog State
  const [bchatDialogOpen, setBchatDialogOpen] = useState(false);
  const [activeSessionForAction, setActiveSessionForAction] = useState<string | null>(null);
  const [bchatConfigs, setBchatConfigs] = useState<Record<string, BChatConfig>>({});
  const [bchatForm, setBchatForm] = useState({
    inboxName: '',
    pending: false,
    reopen: false,
    importContacts: true,
    importMessages: true
  });
  const [associateExistingMode, setAssociateExistingMode] = useState(false);
  const [availableInboxes, setAvailableInboxes] = useState<ChatwootInboxOption[]>([]);
  const [inboxesLoading, setInboxesLoading] = useState(false);
  const [inboxesError, setInboxesError] = useState<string | null>(null);
  const [selectedInboxId, setSelectedInboxId] = useState<number | null>(null);
  const [associating, setAssociating] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const [availableAgents, setAvailableAgents] = useState<ChatwootAgentOption[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [selectedAgentIds, setSelectedAgentIds] = useState<number[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [savingAgents, setSavingAgents] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');

  // Proxy Dialog State
  const [proxyDialogOpen, setProxyDialogOpen] = useState(false);
  const [loadingProxy, setLoadingProxy] = useState(false);
  const [applyingAutoWebshare, setApplyingAutoWebshare] = useState(false);
  const [checkingProxyIp, setCheckingProxyIp] = useState(false);
  const [proxyExternalIp, setProxyExternalIp] = useState<string>('');
  const [proxyIpError, setProxyIpError] = useState<string>('');
  const [proxyGeoInfo, setProxyGeoInfo] = useState<{
    city?: string;
    region?: string;
    country?: string;
    countryCode?: string;
    latitude?: number;
    longitude?: number;
    isp?: string;
    org?: string;
    timezone?: string;
  }>({});
  const [proxyForm, setProxyForm] = useState({
    enabled: false,
    host: '',
    port: '',
    protocol: 'http',
    username: '',
    password: ''
  });

  // Session Settings State
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState<SettingsForm>({
    rejectCall: false,
    msgCall: '',
    groupsIgnore: false,
    alwaysOnline: false,
    readMessages: false,
    syncFullHistory: false,
    readStatus: false
  });

  // Confirmation Dialog State
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText: string;
    type: 'danger' | 'info';
  }>({
    title: '',
    message: '',
    onConfirm: () => { },
    confirmText: 'Confirmar',
    type: 'info'
  });

  const showConfirm = useCallback((config: {
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    type?: 'danger' | 'info';
  }) => {
    setConfirmDialogConfig({
      title: config.title,
      message: config.message,
      onConfirm: config.onConfirm,
      confirmText: config.confirmText || 'Confirmar',
      type: config.type || 'info'
    });
    setConfirmDialogOpen(true);
  }, []);

  const openBchatDialog = useCallback(async (sessionName: string) => {
    setActiveSessionForAction(sessionName);

    setBchatForm({
      inboxName: '',
      pending: false,
      reopen: false,
      importContacts: true,
      importMessages: true
    });
    setAssociateExistingMode(false);
    setSelectedInboxId(null);
    setSelectedAgentIds([]);
    setAvailableAgents([]);
    setAgentsError(null);
    setMembersError(null);
    setAgentSearch('');

    const config = bchatConfigs[sessionName];

    setBchatDialogOpen(true);

    try {
      setInboxesLoading(true);
      setInboxesError(null);
      const response = await authenticatedFetch('/api/chatwoot/inboxes');
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data.inboxes)) {
          const apiInboxes = data.inboxes.filter((i: { channel_type: string; api_channel?: Record<string, unknown> }) => i.channel_type === 'Channel::Api');
          setAvailableInboxes(apiInboxes);
          if (apiInboxes.length > 0 && !selectedInboxId) {
            setSelectedInboxId(apiInboxes[0].id);
          }
        } else {
          setAvailableInboxes([]);
        }
      } else {
        let msg = 'Falha ao carregar caixas';
        try {
          const err = await response.json();
          msg = err?.message || err?.error || msg;
        } catch {
        }
        setAvailableInboxes([]);
        setInboxesError(msg);
      }
    } catch {
      setAvailableInboxes([]);
      setInboxesError('Erro de rede ao carregar caixas');
    } finally {
      setInboxesLoading(false);
    }

    const session = sessions.find(s => s.name === sessionName);
    if (session?.provider === 'WAHA') {
      setAgentsLoading(true);
      setAgentsError(null);
      try {
        const agentsResp = await authenticatedFetch('/api/chatwoot/agents');
        if (agentsResp.ok) {
          const agentsData = await agentsResp.json();
          setAvailableAgents(Array.isArray(agentsData.agents) ? agentsData.agents : []);
        } else {
          const err = await agentsResp.json().catch(() => ({}));
          setAgentsError(err?.message || err?.error || 'Falha ao carregar agentes');
          setAvailableAgents([]);
        }
      } catch {
        setAgentsError('Erro de rede ao carregar agentes');
        setAvailableAgents([]);
      } finally {
        setAgentsLoading(false);
      }

      if (config?.inboxId) {
        setMembersLoading(true);
        setMembersError(null);
        try {
          const membersResp = await authenticatedFetch(`/api/chatwoot/inboxes/${config.inboxId}/members`);
          if (membersResp.ok) {
            const membersData = await membersResp.json();
            if (Array.isArray(membersData.members)) {
              const memberIds = membersData.members.map((m: ChatwootInboxMember) => m.user?.id ?? m.id).filter(Boolean);
              setSelectedAgentIds(memberIds);
            }
          } else {
            const err = await membersResp.json().catch(() => ({}));
            setMembersError(err?.message || err?.error || 'Falha ao carregar membros');
          }
        } catch {
          setMembersError('Erro de rede ao carregar membros');
        } finally {
          setMembersLoading(false);
        }
      }
    }
  }, [authenticatedFetch, bchatConfigs, selectedInboxId, sessions]);

  // Auto-select first inbox if available and none selected (Safety net)
  useEffect(() => {
    if (associateExistingMode && !selectedInboxId && availableInboxes.length > 0) {
      console.log('🔄 Auto-selecionando primeira caixa disponível:', availableInboxes[0]);
      setSelectedInboxId(availableInboxes[0].id);
    }
  }, [associateExistingMode, selectedInboxId, availableInboxes]);

  useEffect(() => {
    const session = sessions.find(s => s.name === activeSessionForAction);
    if (!associateExistingMode || !selectedInboxId || session?.provider !== 'WAHA') return;
    let cancelled = false;
    (async () => {
      setMembersLoading(true);
      setMembersError(null);
      try {
        const resp = await authenticatedFetch(`/api/chatwoot/inboxes/${selectedInboxId}/members`);
        if (cancelled) return;
        if (resp.ok) {
          const data = await resp.json();
          if (Array.isArray(data.members)) {
            const ids = data.members.map((m: ChatwootInboxMember) => m.user?.id ?? m.id).filter(Boolean);
            setSelectedAgentIds(ids);
          }
        } else {
          const err = await resp.json().catch(() => ({}));
          setMembersError(err?.message || err?.error || 'Falha ao carregar membros');
        }
      } catch {
        if (!cancelled) setMembersError('Erro de rede ao carregar membros');
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [associateExistingMode, selectedInboxId, activeSessionForAction, sessions, authenticatedFetch]);

  const openProxyDialog = useCallback(async (sessionName: string) => {
    const session = sessions.find(s => s.name === sessionName);

    setActiveSessionForAction(sessionName);
    setProxyDialogOpen(true);
    setLoadingProxy(true);

    // Reset form
    setProxyForm({
      enabled: false,
      host: '',
      port: '',
      protocol: 'http',
      username: '',
      password: ''
    });
    setProxyExternalIp('');
    setProxyIpError('');
    setProxyGeoInfo({});

    try {
      if (session?.provider === 'EVOLUTION') {
        if (settings?.evolutionHost && settings?.evolutionApiKey) {
          const cleanHost = settings.evolutionHost.replace(/\/$/, "");
          const response = await fetch(`${cleanHost}/proxy/find/${sessionName}`, {
            headers: {
              'apikey': settings.evolutionApiKey
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data && data.Proxy) {
              setProxyForm({
                enabled: data.Proxy.enabled ?? false,
                host: data.Proxy.host || '',
                port: data.Proxy.port?.toString() || '',
                protocol: data.Proxy.protocol || 'http',
                username: data.Proxy.username || '',
                password: data.Proxy.password || ''
              });
            }
          }
        }
      } else {
        // WAHA / QUEPASA / EVOGO (via backend)
        const response = await authenticatedFetch(`/api/waha/sessions/${sessionName}/proxy`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.Proxy) {
            setProxyForm({
              enabled: data.Proxy.enabled ?? false,
              host: data.Proxy.host || '',
              port: data.Proxy.port?.toString() || '',
              protocol: data.Proxy.protocol || 'http',
              username: data.Proxy.username || '',
              password: data.Proxy.password || ''
            });
          }
        }
      }
    } catch {
      console.error('Error fetching proxy config');
    } finally {
      setLoadingProxy(false);
    }
  }, [sessions, settings?.evolutionHost, settings?.evolutionApiKey, authenticatedFetch]);

  const handleSaveProxy = useCallback(async () => {
    if (!activeSessionForAction) return;

    const session = sessions.find(s => s.name === activeSessionForAction);

    if (session?.provider === 'EVOLUTION') {
      if (!settings?.evolutionHost || !settings?.evolutionApiKey) {
        toast.error("Configurações da Evolution API não encontradas");
        return;
      }

      if (proxyForm.enabled && (!proxyForm.host || !proxyForm.port)) {
        toast.error("Host e Porta são obrigatórios quando o proxy está ativado");
        return;
      }

      try {
        const cleanHost = settings.evolutionHost.replace(/\/$/, "");
        const response = await fetch(`${cleanHost}/proxy/set/${activeSessionForAction}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': settings.evolutionApiKey
          },
          body: JSON.stringify({
            enabled: proxyForm.enabled,
            host: proxyForm.host,
            port: proxyForm.port,
            protocol: proxyForm.protocol,
            username: proxyForm.username,
            password: proxyForm.password
          })
        });

        if (response.ok) {
          toast.success("Configuração de Proxy atualizada!");
          setProxyDialogOpen(false);
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(`Erro ao salvar proxy: ${err.message || response.statusText}`);
        }
      } catch {
        toast.error("Erro ao conectar com a API");
      }
    } else {
      // WAHA / QUEPASA / EVOGO (via backend)
      if (proxyForm.enabled && (!proxyForm.host || !proxyForm.port)) {
        toast.error("Host e Porta são obrigatórios quando o proxy está ativado");
        return;
      }

      try {
        const response = await authenticatedFetch(`/api/waha/sessions/${activeSessionForAction}/proxy`, {
          method: 'POST',
          body: JSON.stringify({
            enabled: proxyForm.enabled,
            host: proxyForm.host,
            port: proxyForm.port,
            protocol: proxyForm.protocol,
            username: proxyForm.username,
            password: proxyForm.password
          })
        });

        if (response.ok) {
          toast.success("Configuração de Proxy atualizada!");
          setProxyDialogOpen(false);
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(`Erro ao salvar proxy: ${err.message || response.statusText}`);
        }
      } catch {
        toast.error("Erro ao conectar com a API");
      }
    }
  }, [activeSessionForAction, proxyForm, settings?.evolutionHost, settings?.evolutionApiKey, sessions, authenticatedFetch]);

  const handleApplyAutomaticWebshareProxy = useCallback(async () => {
    if (!activeSessionForAction) return;
    const session = sessions.find((s) => s.name === activeSessionForAction);
    if (!['EVOGO', 'WAHA'].includes(session?.provider || '')) {
      toast.error('Aplicação automática Webshare disponível apenas para EVOGO e WAHA');
      return;
    }

    try {
      setApplyingAutoWebshare(true);
      const response = await authenticatedFetch(`/api/waha/sessions/${activeSessionForAction}/proxy/auto-webshare`, {
        method: 'POST'
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data.error || 'Falha ao aplicar proxy automático Webshare');
        return;
      }

      if (data?.proxy) {
        setProxyForm({
          enabled: data.proxy.enabled ?? false,
          host: data.proxy.host || '',
          port: data.proxy.port?.toString() || '',
          protocol: data.proxy.protocol || 'http',
          username: data.proxy.username || '',
          password: data.proxy.password || ''
        });
      }

      toast.success('Proxy Webshare aplicado automaticamente');
      loadSessions(false);
    } catch (error) {
      console.error('Erro ao aplicar proxy automático Webshare:', error);
      toast.error('Erro ao aplicar proxy automático Webshare');
    } finally {
      setApplyingAutoWebshare(false);
    }
  }, [activeSessionForAction, sessions, authenticatedFetch, loadSessions]);

  const handleCheckProxyIp = useCallback(async () => {
    if (!activeSessionForAction) return;

    try {
      setCheckingProxyIp(true);
      setProxyIpError('');
      setProxyExternalIp('');
      setProxyGeoInfo({});

      const response = await authenticatedFetch(`/api/waha/sessions/${activeSessionForAction}/proxy/ip`);
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = data?.error || 'Falha ao consultar IP externo via proxy';
        setProxyIpError(message);
        toast.error(message);
        return;
      }

      const ip = typeof data?.externalIp === 'string' ? data.externalIp : '';
      if (!ip) {
        setProxyIpError('Não foi possível identificar o IP externo');
        return;
      }

      setProxyExternalIp(ip);
      const geo = (data && typeof data === 'object' && data.geolocation && typeof data.geolocation === 'object')
        ? data.geolocation as Record<string, unknown>
        : {};
      setProxyGeoInfo({
        city: typeof geo.city === 'string' ? geo.city : undefined,
        region: typeof geo.region === 'string' ? geo.region : undefined,
        country: typeof geo.country === 'string' ? geo.country : undefined,
        countryCode: typeof geo.countryCode === 'string' ? geo.countryCode : undefined,
        latitude: typeof geo.latitude === 'number' ? geo.latitude : undefined,
        longitude: typeof geo.longitude === 'number' ? geo.longitude : undefined,
        isp: typeof geo.isp === 'string' ? geo.isp : undefined,
        org: typeof geo.org === 'string' ? geo.org : undefined,
        timezone: typeof geo.timezone === 'string' ? geo.timezone : undefined
      });
      toast.success(`IP externo atual: ${ip}`);
    } catch (error) {
      console.error('Erro ao consultar IP externo via proxy:', error);
      const message = 'Erro ao consultar IP externo via proxy';
      setProxyIpError(message);
      toast.error(message);
    } finally {
      setCheckingProxyIp(false);
    }
  }, [activeSessionForAction, authenticatedFetch]);

  const openSettingsDialog = useCallback(async (sessionName: string) => {
    const session = sessions.find(s => s.name === sessionName);
    if (isEvogoProvider(session?.provider)) {
      toast.error('Configurações avançadas ainda não estão disponíveis para instâncias Evogo');
      return;
    }

    setActiveSessionForAction(sessionName);
    setSettingsDialogOpen(true);
    setLoadingSettings(true);

    // Default form
    setSettingsForm({
      rejectCall: false,
      msgCall: '',
      groupsIgnore: false,
      alwaysOnline: false,
      readMessages: false,
      syncFullHistory: false,
      readStatus: false
    });

    try {
      if (session?.provider === 'EVOLUTION') {
        if (settings?.evolutionHost && settings?.evolutionApiKey) {
          const cleanHost = settings.evolutionHost.replace(/\/$/, "");
          const response = await fetch(`${cleanHost}/settings/find/${sessionName}`, {
            headers: {
              'apikey': settings.evolutionApiKey
            }
          });

          if (response.ok) {
            const data = await response.json();
            // Evolution API can return settings nested under 'settings', 'Setting', or at the top level
            const s = data?.settings || data?.Setting || data;
            if (s) {
              setSettingsForm({
                rejectCall: s.rejectCall ?? false,
                msgCall: s.msgCall || '',
                groupsIgnore: s.groupsIgnore ?? false,
                alwaysOnline: s.alwaysOnline ?? false,
                readMessages: s.readMessages ?? false,
                syncFullHistory: s.syncFullHistory ?? false,
                readStatus: s.readStatus ?? false
              });
            }
          }
        }
      } else {
        // WAHA / QUEPASA
        const response = await authenticatedFetch(`/api/waha/sessions/${sessionName}/settings`);
        if (response.ok) {
          const data = await response.json();
          const s = data?.settings || data;
          if (s) {
            setSettingsForm({
              rejectCall: s.rejectCall ?? false,
              msgCall: s.msgCall || '',
              groupsIgnore: s.groupsIgnore ?? false,
              alwaysOnline: s.alwaysOnline ?? false,
              readMessages: s.readMessages ?? false,
              syncFullHistory: s.syncFullHistory ?? false,
              readStatus: s.readStatus ?? false
            });
          }
        }
      }
    } catch {
      console.error('Error fetching session settings');
    } finally {
      setLoadingSettings(false);
    }
  }, [sessions, settings?.evolutionHost, settings?.evolutionApiKey, authenticatedFetch]);

  const handleSaveSettings = useCallback(async () => {
    if (!activeSessionForAction) return;

    const session = sessions.find(s => s.name === activeSessionForAction);
    if (isEvogoProvider(session?.provider)) {
      toast.error('Configurações avançadas ainda não estão disponíveis para instâncias Evogo');
      return;
    }

    if (session?.provider === 'EVOLUTION') {
      if (!settings?.evolutionHost || !settings?.evolutionApiKey) {
        toast.error("Configurações da Evolution API não encontradas");
        return;
      }

      try {
        const cleanHost = settings.evolutionHost.replace(/\/$/, "");
        // Using /settings/set as it's the standard for POST in Evolution API
        const response = await fetch(`${cleanHost}/settings/set/${activeSessionForAction}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': settings.evolutionApiKey
          },
          body: JSON.stringify({
            rejectCall: settingsForm.rejectCall,
            msgCall: settingsForm.msgCall,
            groupsIgnore: settingsForm.groupsIgnore,
            alwaysOnline: settingsForm.alwaysOnline,
            readMessages: settingsForm.readMessages,
            syncFullHistory: settingsForm.syncFullHistory,
            readStatus: settingsForm.readStatus
          })
        });

        if (response.ok) {
          toast.success("Configurações atualizadas!");
          setSettingsDialogOpen(false);
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(`Erro ao salvar configurações: ${err.message || response.statusText}`);
        }
      } catch {
        toast.error("Erro ao conectar com a API");
      }
    } else {
      // WAHA / QUEPASA
      try {
        const response = await authenticatedFetch(`/api/waha/sessions/${activeSessionForAction}/settings`, {
          method: 'POST',
          body: JSON.stringify({
            rejectCall: settingsForm.rejectCall,
            msgCall: settingsForm.msgCall,
            groupsIgnore: settingsForm.groupsIgnore,
            alwaysOnline: settingsForm.alwaysOnline,
            readMessages: settingsForm.readMessages,
            syncFullHistory: settingsForm.syncFullHistory,
            readStatus: settingsForm.readStatus
          })
        });

        if (response.ok) {
          toast.success("Configurações atualizadas!");
          setSettingsDialogOpen(false);
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(`Erro ao salvar configurações: ${err.message || response.statusText}`);
        }
      } catch {
        toast.error("Erro ao conectar com a API");
      }
    }
  }, [activeSessionForAction, settingsForm, settings?.evolutionHost, settings?.evolutionApiKey, sessions, authenticatedFetch]);

  const handleCreateBChatInbox = useCallback(async () => {
    if (!activeSessionForAction || isCreating) return;
    setIsCreating(true);
    const session = sessions.find(s => s.name === activeSessionForAction);

    // Check Settings Common
    if (!settings?.chatwootAccountId || !settings?.chatwootApiToken || !settings?.chatwootUrl) {
      toast.error("Configurações do BChat não encontradas (Integrações)");
      setIsCreating(false);
      return;
    }

    if (!bchatForm.inboxName.trim()) {
      toast.error('Informe um nome para a caixa de entrada');
      setIsCreating(false);
      return;
    }

    if (session?.provider === 'EVOLUTION' || session?.provider === 'EVOGO') {
      try {
        const response = await authenticatedFetch(`/api/chatwoot/sessions/${activeSessionForAction}/inbox`, {
          method: 'POST',
          body: JSON.stringify({
            autoCreate: true,
            inboxName: bchatForm.inboxName.trim(),
            agentUserIds: selectedAgentIds.length > 0 ? selectedAgentIds : undefined,
            options: {
              pending: bchatForm.pending,
              reopen: bchatForm.reopen,
              importContacts: bchatForm.importContacts,
              importMessages: bchatForm.importMessages
            }
          })
        });

        if (response.ok) {
          toast.success("Caixa de entrada criada no BChat com sucesso!");
          setBchatDialogOpen(false);
          await loadSessions(false);
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(`Erro ao criar inbox: ${err.message || response.statusText}`);
        }
      } catch {
        toast.error("Erro ao conectar com a API");
      } finally {
        setIsCreating(false);
      }
    } else {
      // WAHA / QUEPASA
      try {
        // 1. Create Inbox
        const createResponse = await authenticatedFetch('/api/chatwoot/inboxes', {
          method: 'POST',
          body: JSON.stringify({ name: bchatForm.inboxName })
        });

        if (!createResponse.ok) {
          const err = await createResponse.json().catch(() => ({}));
          throw new Error(err.message || 'Erro ao criar inbox no Chatwoot');
        }

        const createdInbox = await createResponse.json();

        // 2. Link Session
        const linkResponse = await authenticatedFetch(`/api/chatwoot/sessions/${activeSessionForAction}/inbox`, {
          method: 'POST',
          body: JSON.stringify({
            inboxId: createdInbox.id,
            inboxName: createdInbox.name,
            inboxIdentifier: createdInbox.api_channel?.identifier,
            agentUserIds: selectedAgentIds.length > 0 ? selectedAgentIds : undefined,
            options: {
              pending: bchatForm.pending,
              reopen: bchatForm.reopen,
              importContacts: bchatForm.importContacts,
              importMessages: bchatForm.importMessages
            }
          })
        });

        if (!linkResponse.ok) {
          const err = await linkResponse.json().catch(() => ({}));
          throw new Error(err.message || 'Erro ao vincular conexão à inbox');
        }

        toast.success("Caixa de entrada criada e vinculada no BChat com sucesso!");
        setBchatDialogOpen(false);
        await loadSessions(false);

      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Erro ao criar/vincular inbox");
      } finally {
        setIsCreating(false);
      }
    }
  }, [activeSessionForAction, bchatForm, settings, authenticatedFetch, loadSessions, sessions, isCreating, selectedAgentIds]);

  const handleAssociateExistingInbox = useCallback(async () => {
    console.log('⚡ handleAssociateExistingInbox chamado', { activeSessionForAction, selectedInboxId });
    if (!activeSessionForAction || !selectedInboxId) {
      toast.error('Selecione uma caixa para associar');
      return;
    }
    try {
      setAssociating(true);
      console.log('🔧 Associando sessão à caixa existente:', {
        session: activeSessionForAction,
        inboxId: selectedInboxId
      });
      const selectedInbox = availableInboxes.find(i => i.id === selectedInboxId);
      const response = await authenticatedFetch(`/api/chatwoot/sessions/${activeSessionForAction}/inbox`, {
        method: 'POST',
        body: JSON.stringify({
          inboxId: selectedInboxId,
          inboxName: selectedInbox?.name,
          inboxIdentifier: selectedInbox?.api_channel?.identifier,
          agentUserIds: selectedAgentIds.length > 0 ? selectedAgentIds : undefined,
          options: {
            pending: bchatForm.pending,
            reopen: bchatForm.reopen,
            importContacts: bchatForm.importContacts,
            importMessages: bchatForm.importMessages
          }
        })
      });
      if (response.ok) {
        if (selectedAgentIds.length > 0) {
          try {
            setSavingAgents(true);
            const syncResp = await authenticatedFetch(`/api/chatwoot/inboxes/${selectedInboxId}/members`, {
              method: 'PATCH',
              body: JSON.stringify({ userIds: selectedAgentIds })
            });
            if (!syncResp.ok) {
              const err = await syncResp.json().catch(() => ({}));
              console.warn('Aviso: associacao ok mas sincronizacao de agentes falhou:', err);
            }
          } catch (syncErr) {
            console.warn('Aviso: associacao ok mas sincronizacao de agentes falhou:', syncErr);
          } finally {
            setSavingAgents(false);
          }
        }
        toast.success('Conexão associada à caixa existente');
        setBchatConfigs(prev => ({
          ...prev,
          [activeSessionForAction]: {
            enabled: true,
            inboxId: selectedInboxId,
            nameInbox: availableInboxes.find(i => i.id === selectedInboxId)?.name,
            reopenConversation: bchatForm.reopen,
            conversationPending: bchatForm.pending,
            importContacts: bchatForm.importContacts,
            importMessages: bchatForm.importMessages
          }
        }));
        setBchatDialogOpen(false);
        await loadSessions(false);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(`Erro ao associar: ${err.message || response.statusText}`);
      }
    } catch {
      toast.error("Erro ao conectar com a API");
    } finally {
      setAssociating(false);
    }
  }, [activeSessionForAction, selectedInboxId, authenticatedFetch, availableInboxes, bchatForm, loadSessions, selectedAgentIds]);

  const handleDisconnectBchat = useCallback(async () => {
    if (!activeSessionForAction) return;
    try {
      setDisconnecting(true);
      const response = await authenticatedFetch(`/api/chatwoot/sessions/${activeSessionForAction}/disconnect`, {
        method: 'POST'
      });
      if (response.ok) {
        setBchatConfigs(prev => {
          const next = { ...prev };
          delete next[activeSessionForAction];
          return next;
        });
        toast.success('Integração BChat desconectada');
        setBchatDialogOpen(false);
        await loadSessions(false);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(`Erro ao desconectar: ${err.message || response.statusText}`);
      }
    } catch {
      toast.error('Erro ao desconectar');
    } finally {
      setDisconnecting(false);
    }
  }, [activeSessionForAction, authenticatedFetch, loadSessions]);

  const handleSaveAgents = useCallback(async () => {
    const config = activeSessionForAction ? bchatConfigs[activeSessionForAction] : null;
    if (!config?.inboxId) {
      toast.error('Nenhuma caixa configurada');
      return;
    }
    try {
      setSavingAgents(true);
      const resp = await authenticatedFetch(`/api/chatwoot/inboxes/${config.inboxId}/members`, {
        method: 'PATCH',
        body: JSON.stringify({ userIds: selectedAgentIds })
      });
      if (resp.ok) {
        toast.success('Agentes atualizados com sucesso');
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(`Erro ao salvar agentes: ${err.message || resp.statusText}`);
      }
    } catch {
      toast.error('Erro ao salvar agentes');
    } finally {
      setSavingAgents(false);
    }
  }, [activeSessionForAction, bchatConfigs, selectedAgentIds, authenticatedFetch]);

  const filteredAgents = availableAgents.filter(a => {
    if (!agentSearch) return true;
    const q = agentSearch.toLowerCase();
    return a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q) || a.available_name.toLowerCase().includes(q);
  });

  // Preload das imagens dos provedores para carregamento instantâneo
  useEffect(() => {
    const images = [
      '/iconewaha.png',
      '/iconeevolutionapi.png',
      '/iconequepasa.png',
      '/iconeevogo.svg'
    ];

    images.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Carregar provedores permitidos do tenant
  const loadAllowedProviders = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/tenants/allowed-providers');
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.allowedProviders)) {
          const normalizedProviders = data.allowedProviders.filter((provider: string): provider is VisibleSessionProvider =>
            DEFAULT_ALLOWED_PROVIDERS.includes(provider as VisibleSessionProvider)
          );
          setAllowedProviders(normalizedProviders.length > 0 ? normalizedProviders : DEFAULT_ALLOWED_PROVIDERS);
          // Definir o primeiro provedor permitido como padrão
          if (data.allowedProviders.length > 0 && !data.allowedProviders.includes(newSessionProvider)) {
            const firstProvider = normalizedProviders[0] || 'WAHA';
            setNewSessionProvider(firstProvider);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar provedores permitidos:', error);
    }
  }, [authenticatedFetch, newSessionProvider]);

  useEffect(() => {
    // Não carregar sessões até que o tenant esteja definido
    if (tenantLoading || !selectedTenantId) {
      return;
    }

    // Carregar provedores permitidos
    loadAllowedProviders();

    // Recarregar sessões quando o tenant mudar
    loadSessions();

    // Iniciar polling a cada 5 segundos
    const interval = setInterval(() => {
      loadSessions(false); // Não mostrar loading durante polling automático
    }, 5000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [selectedTenantId, tenantLoading, loadAllowedProviders, loadSessions]); // Recarrega quando o tenant ou filtros mudarem

  // Polling para verificar se a conexão foi estabelecida quando o modal QR está aberto
  useEffect(() => {
    if (!qrModalOpen || !currentQRSession) {
      return;
    }

    const checkConnection = async () => {
      try {
        const response = await authenticatedFetch('/api/waha/sessions');
        if (response.ok) {
          const sessionsData = await response.json();
          const updatedSession = sessionsData.find((s: WhatsAppSession) => s.name === currentQRSession.name);

          if (updatedSession) {
            console.log(`🔍 Polling - sessão ${currentQRSession.name}:`, {
              provider: updatedSession.provider,
              status: updatedSession.status,
              me: updatedSession.me
            });

            // Se a sessão mudou para WORKING (conectada)
            if (updatedSession.status === 'WORKING') {
              console.log(`✅ Sessão ${currentQRSession.name} conectada! Fechando modal...`);

              // Fechar modal
              setQrModalOpen(false);
              setCurrentQRSession(null);

              // Atualizar lista de sessões imediatamente
              await loadSessions(false);

              // Mostrar notificação de sucesso
              const userName = updatedSession.me?.pushName || 'Usuário';
              toast.success(`WhatsApp conectado com sucesso! Logado como: ${userName}`);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status da conexão:', error);
      }
    };

    // Verificar imediatamente
    checkConnection();

    // Depois verificar a cada 2 segundos (polling mais frequente)
    const interval = setInterval(checkConnection, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [qrModalOpen, currentQRSession, authenticatedFetch, loadSessions]);

  const createSession = useCallback(async () => {
    if (!newSessionName.trim()) {
      toast.error('Digite um nome para a conexão');
      return;
    }

    // Validar se as configurações do provedor estão disponíveis
    if (newSessionProvider === 'WAHA' && (!settings?.wahaHost || !settings?.wahaApiKey)) {
      toast.error('Configure as credenciais WAHA nas configurações do sistema');
      return;
    }

    if (newSessionProvider === 'EVOLUTION' && (!settings?.evolutionHost || !settings?.evolutionApiKey)) {
      toast.error('Configure as credenciais Evolution API nas configurações do sistema');
      return;
    }

    if (newSessionProvider === 'QUEPASA' && (!settings?.quepasaUrl || !settings?.quepasaLogin || !settings?.quepasaPassword)) {
      toast.error('Configure as credenciais Quepasa nas configurações do sistema');
      return;
    }

    setIsCreating(true);
    try {
      const response = await authenticatedFetch('/api/waha/sessions', {
        method: 'POST',
        body: JSON.stringify({
          name: newSessionName.trim(),
          provider: newSessionProvider,
          interactiveCampaignEnabled
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));

        // Verificar se é erro de quota
        if (errorData.upgradeRequired || (errorData.message && errorData.message.includes('Limite'))) {
          toast.error(errorData.message || 'Limite de conexões atingido. Faça upgrade do seu plano para continuar.', {
            duration: 6000,
            icon: '⚠️'
          });
          throw new Error(errorData.message);
        }

        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const createdSessionData = await response.json();

      toast.success(`Conexão ${getProviderMeta(newSessionProvider).label} criada com sucesso`);
      setNewSessionName('');
      setNewSessionProvider(allowedProviders[0] || 'WAHA');

      // Recarregar imediatamente
      await loadSessions();

      // Se for Evolution e veio QR code, abrir modal automaticamente
      if (newSessionProvider === 'EVOLUTION' && createdSessionData.qrcode?.base64) {
        setTimeout(async () => {
          // Buscar o nome real da sessão criada
          const sessionsResp = await authenticatedFetch('/api/waha/sessions').then(r => r.json());
          const createdSession = sessionsResp.find((s: WhatsAppSession) => s.name.startsWith(newSessionName.trim()));

          if (createdSession) {
            await openQRModal(createdSession.name);
          }
        }, 1000);
      }

      // Aguardar mais um pouco e recarregar novamente para pegar status atualizado
      setTimeout(() => {
        loadSessions(false);
      }, 2000);
    } catch (error) {
      console.error('Erro ao criar conexão:', error);
      // Não mostrar toast de erro genérico se já mostramos o toast específico de quota
      if (!(error instanceof Error && error.message.includes('Limite'))) {
        toast.error(error instanceof Error ? error.message : 'Erro ao criar conexão');
      }
    } finally {
      setIsCreating(false);
    }
  }, [newSessionName, newSessionProvider, interactiveCampaignEnabled, settings, authenticatedFetch, loadSessions, openQRModal, allowedProviders]);

  const deleteSession = useCallback(async (sessionName: string) => {
    showConfirm({
      title: 'Remover Conexão',
      message: `Tem certeza que deseja remover a conexão "${sessionName}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Remover',
      type: 'danger',
      onConfirm: async () => {
        try {
          const url = `/api/waha/sessions/${sessionName}`;

          const response = await authenticatedFetch(url, {
            method: 'DELETE'
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
            throw new Error(errorData.error || `HTTP ${response.status}`);
          }

          const result = await response.json().catch(() => ({}));

          if (Array.isArray(result.warnings) && result.warnings.length > 0) {
            toast.success('Conexão removida com limpeza local');
          } else {
            toast.success('Conexão removida com sucesso');
          }
          loadSessions();
        } catch (error) {
          console.error('Erro ao remover conexão:', error);
          toast.error(error instanceof Error ? error.message : 'Erro ao remover conexão');
        }
      }
    });
  }, [showConfirm, authenticatedFetch, loadSessions, sessions]);

  const restartSession = useCallback(async (sessionName: string) => {
    try {
      const session = sessions.find(s => s.name === sessionName);

      if (session?.provider === 'EVOLUTION') {
        if (!settings?.evolutionHost || !settings?.evolutionApiKey) {
          throw new Error("Configurações da Evolution API não encontradas");
        }

        const cleanHost = settings.evolutionHost.replace(/\/$/, "");
        const targetUrl = `${cleanHost}/instance/restart/${sessionName}`;

        const response = await fetch(targetUrl, {
          method: 'POST',
          headers: {
            'apikey': settings.evolutionApiKey
          }
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Erro ao reiniciar na Evolution: ${text}`);
        }
      } else {
        // Fallback or WAHA
        const response = await authenticatedFetch(`/api/waha/sessions/${sessionName}/restart`, {
          method: 'POST'
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }
      }

      toast.success('Conexão reiniciada');

      // Aguardar um pouco antes de recarregar
      setTimeout(() => {
        loadSessions(false);
      }, 1000);
    } catch (error) {
      console.error('Erro ao reiniciar conexão:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao reiniciar conexão');
    }
  }, [sessions, settings?.evolutionHost, settings?.evolutionApiKey, authenticatedFetch, loadSessions]);

  const requestQRCode = useCallback(async (sessionName: string) => {
    setLoadingQR(sessionName);
    try {
      // Primeiro, iniciar a sessão
      const startResponse = await authenticatedFetch(`/api/waha/sessions/${sessionName}/start`, {
        method: 'POST'
      });

      if (!startResponse.ok) {
        const errorData = await startResponse.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `HTTP ${startResponse.status}`);
      }

      toast.success('QR Code solicitado. Aguarde...');

      // Aguardar um pouco e abrir modal para mostrar QR
      setTimeout(async () => {
        await openQRModal(sessionName, { skipStart: true });
        loadSessions(false);
      }, 2000);

    } catch (error) {
      console.error('Erro ao solicitar QR Code:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao solicitar QR Code');
    } finally {
      setLoadingQR(null);
    }
  }, [authenticatedFetch, loadSessions, openQRModal]);

  const closeQRModal = useCallback(() => {
    if (qrRetryTimeoutRef.current) {
      clearTimeout(qrRetryTimeoutRef.current);
      qrRetryTimeoutRef.current = null;
    }
    setQrModalOpen(false);
    setCurrentQRSession(null);
    // Recarregar sessões para verificar se conectou
    loadSessions(false);
  }, [loadSessions]);

  useEffect(() => {
    return () => {
      if (qrRetryTimeoutRef.current) {
        clearTimeout(qrRetryTimeoutRef.current);
        qrRetryTimeoutRef.current = null;
      }
    };
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'WORKING':
        return 'bg-green-400/20 text-green-300 border border-green-400/30';
      case 'SCAN_QR_CODE':
        return 'bg-yellow-400/20 text-yellow-300 border border-yellow-400/30';
      case 'STOPPED':
        return 'bg-white/10 text-white/60 border border-white/20';
      case 'FAILED':
        return 'bg-red-400/20 text-red-300 border border-red-400/30';
      default:
        return 'bg-white/10 text-white/60 border border-white/20';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'WORKING':
        return 'Conectado';
      case 'SCAN_QR_CODE':
        return 'Aguardando QR Code';
      case 'STOPPED':
        return 'Parado';
      case 'FAILED':
        return 'Erro';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] gap-4">
        <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
        <div className="text-gray-400 font-oswald uppercase tracking-[0.2em] font-bold text-sm">Carregando conexões...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] text-white">
      <Header
        title="Conexões WhatsApp"
        subtitle={`${sessions.length} ${sessions.length === 1 ? 'conexão ativa' : 'conexões ativas'}`}
        compact
        className="rounded-b-[2rem]"
        actions={
          <div className="flex gap-4">
            <button
              onClick={() => loadSessions()}
              className="px-6 py-3 bg-white/5 text-gray-400 text-[10px] rounded-xl font-bold hover:bg-white/10 transition-all border border-white/10 uppercase tracking-[0.2em] font-oswald"
            >
              Atualizar
            </button>
            <button
              onClick={() => setCreateSessionModalOpen(true)}
              className="px-8 py-3 bg-blue-600 text-white text-[10px] rounded-xl font-bold hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all flex items-center justify-center gap-3 uppercase tracking-[0.2em] border border-blue-500 relative overflow-hidden group/btn font-oswald"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
              <span className="text-base group-hover:rotate-12 transition-transform">➕</span> Nova Conexão
            </button>
          </div>
        }
      />

      <div className="p-4 space-y-4 animate-fadeIn max-w-[1600px] mx-auto">

        {/* Filtros */}
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] p-8 flex flex-wrap items-center gap-10 relative overflow-hidden group/filters">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover/filters:opacity-100 transition-opacity" />

          <div className="flex flex-col gap-3 relative z-10">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">ORDENAR POR</span>
            <div className="flex items-center gap-3">
              <select
                className="bg-white/5 border border-white/10 rounded-xl px-5 py-3 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all cursor-pointer font-oswald uppercase tracking-wider min-w-[140px]"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'status' | 'provider')}>
                <option value="name" className="bg-[#1e293b]">Nome</option>
                <option value="status" className="bg-[#1e293b]">Status</option>
                <option value="provider" className="bg-[#1e293b]">Provedor</option>
              </select>
              <select
                className="bg-white/5 border border-white/10 rounded-xl px-5 py-3 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all cursor-pointer font-oswald uppercase tracking-wider"
                value={sortDir}
                onChange={(e) => setSortDir(e.target.value as 'asc' | 'desc')}>
                <option value="asc" className="bg-[#1e293b]">ASC</option>
                <option value="desc" className="bg-[#1e293b]">DESC</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-3 relative z-10">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">PROVEDOR</span>
            <select
              className="bg-white/5 border border-white/10 rounded-xl px-5 py-3 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all cursor-pointer font-oswald uppercase tracking-wider min-w-[160px]"
              value={filterProvider}
              onChange={(e) => setFilterProvider(e.target.value as SessionFilterProvider)}>
              <option value="ALL" className="bg-[#1e293b]">Todos</option>
              <option value="WAHA" className="bg-[#1e293b]">WAHA</option>
              <option value="EVOLUTION" className="bg-[#1e293b]">EVOLUTION API</option>
              <option value="QUEPASA" className="bg-[#1e293b]">QUEPASA</option>
              <option value="EVOGO" className="bg-[#1e293b]">EVOGO</option>
            </select>
          </div>

          <div className="flex flex-col gap-3 relative z-10">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">STATUS</span>
            <select
              className="bg-white/5 border border-white/10 rounded-xl px-5 py-3 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all cursor-pointer font-oswald uppercase tracking-wider min-w-[160px]"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'ALL' | 'WORKING' | 'SCAN_QR_CODE' | 'STOPPED' | 'FAILED')}>
              <option value="ALL" className="bg-[#1e293b]">Todos</option>
              <option value="WORKING" className="bg-[#1e293b]">Conectado</option>
              <option value="SCAN_QR_CODE" className="bg-[#1e293b]">QR Code</option>
              <option value="STOPPED" className="bg-[#1e293b]">Parado</option>
              <option value="FAILED" className="bg-[#1e293b]">Falhou</option>
            </select>
          </div>
        </div>

        {/* Lista de Conexões */}
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden relative group/list">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 to-transparent pointer-events-none" />

          <div className="px-8 py-6 border-b border-white/10 flex items-center justify-between relative z-10">
            <div className="flex items-center gap-4">
              <div className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></div>
              <h3 className="text-xl font-bold text-white font-oswald uppercase tracking-tight">Conexões Ativas</h3>
            </div>
            <div className="px-4 py-1.5 bg-white/5 rounded-full border border-white/10 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald">
              {sessions.length} Conexões
            </div>
          </div>

          {sessions.length === 0 ? (
            <div className="p-24 text-center relative z-10">
              <div className="bg-white/5 w-24 h-24 rounded-[2rem] flex items-center justify-center mx-auto mb-8 border border-white/10 shadow-inner group/empty-icon">
                <Globe className="text-gray-500 opacity-50 group-hover/empty-icon:scale-110 group-hover/empty-icon:rotate-12 transition-all duration-500" size={48} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 font-oswald uppercase tracking-tight">Nenhuma conexão encontrada</h3>
              <p className="text-gray-500 font-oswald text-[10px] font-bold uppercase tracking-[0.2em]">Clique em "Nova Conexão" para começar a conectar suas contas.</p>
            </div>
          ) : (
            <div className="p-8 grid grid-cols-1 gap-6 relative z-10">
              {sessions.map((session) => (
                <div key={session.name} className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-all duration-500 group/card hover:shadow-[0_0_40px_rgba(37,99,235,0.1)] hover:border-blue-500/30 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity" />

                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 relative z-10">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-4 mb-4">
                        <h4 className="text-2xl font-bold text-white font-oswald truncate group-hover/card:text-blue-400 transition-colors uppercase tracking-tight">{session.displayName || session.name}</h4>

                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-sm transition-all group-hover/card:scale-105 ${getProviderMeta(session.provider).badgeClass}`}>
                            <img
                              src={getProviderMeta(session.provider).icon}
                              alt={session.provider}
                              className="w-4 h-4 object-contain"
                            />
                            {getProviderMeta(session.provider).label}
                          </span>

                          <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-sm transition-all group-hover/card:scale-105 ${getStatusColor(session.status)}`}>
                            {getStatusText(session.status)}
                          </span>

                          {bchatConfigs[session.name] && (
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shadow-sm transition-all group-hover/card:scale-105">
                              <Check size={12} strokeWidth={3} /> BChat
                            </span>
                          )}

                          {(session.config?.proxy as { enabled?: boolean } | undefined)?.enabled && (
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 shadow-sm transition-all group-hover/card:scale-105">
                              <Globe size={12} strokeWidth={3} /> Proxy On
                            </span>
                          )}
                        </div>
                      </div>

                      {session.me ? (
                        <div className="flex items-center gap-4 p-4 bg-white/5 rounded-2xl border border-white/10 group-hover/card:bg-white/10 transition-colors">
                          <div className="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                            <span className="text-xl">📱</span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest font-oswald">Conectado como</span>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-white font-oswald text-base">{session.me.pushName}</span>
                              <span className="text-green-400 font-bold font-oswald text-sm tracking-wider">({session.me.id.split('@')[0]})</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-600"></span>
                          Aguardando pareamento do dispositivo
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3 ml-auto">
                      {session.status === 'STOPPED' && (
                        <button
                          onClick={() => requestQRCode(session.name)}
                          disabled={loadingQR === session.name}
                          className="px-8 py-3 bg-green-600 text-white text-[10px] rounded-xl font-bold hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] transition-all flex items-center justify-center gap-3 uppercase tracking-[0.2em] border border-green-500 relative overflow-hidden group/btn-conn font-oswald disabled:opacity-50"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn-conn:animate-shimmer" />
                          {loadingQR === session.name ? (
                            <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                          ) : (
                            <span className="text-base group-hover:scale-110 transition-transform">🔌</span>
                          )}
                          {loadingQR === session.name ? 'Solicitando...' : 'Conectar'}
                        </button>
                      )}
                      {session.status === 'SCAN_QR_CODE' && (
                        <button
                          onClick={() => openQRModal(session.name)}
                          className="px-8 py-3 bg-blue-600 text-white text-[10px] rounded-xl font-bold hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all flex items-center justify-center gap-3 uppercase tracking-[0.2em] border border-blue-500 relative overflow-hidden group/btn-qr font-oswald"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn-qr:animate-shimmer" />
                          <span className="text-base group-hover:scale-110 transition-transform">📸</span> Ver QR Code
                        </button>
                      )}

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="w-12 h-12 flex items-center justify-center text-gray-400 bg-white/5 hover:bg-white/10 hover:text-white rounded-xl transition-all border border-white/10 group/btn-more"
                          >
                            <svg className="w-6 h-6 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                            </svg>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 text-white rounded-2xl p-2 min-w-[200px] shadow-2xl animate-in fade-in zoom-in duration-200">
                          <>
                            <DropdownMenuItem
                              onClick={() => openBchatDialog(session.name)}
                              className="flex items-center gap-3 px-4 py-3 focus:bg-blue-600/20 focus:text-blue-400 cursor-pointer rounded-xl transition-colors font-oswald uppercase text-[10px] font-bold tracking-widest"
                            >
                              <span className="text-lg">💬</span> BChat
                            </DropdownMenuItem>
                            {!isEvogoProvider(session.provider) && (
                              <>
                              <DropdownMenuItem
                                onClick={() => openSettingsDialog(session.name)}
                                className="flex items-center gap-3 px-4 py-3 focus:bg-blue-600/20 focus:text-blue-400 cursor-pointer rounded-xl transition-colors font-oswald uppercase text-[10px] font-bold tracking-widest"
                              >
                                <span className="text-lg">⚙️</span> Configurações
                              </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem
                              onClick={() => openProxyDialog(session.name)}
                              className="flex items-center gap-3 px-4 py-3 focus:bg-blue-600/20 focus:text-blue-400 cursor-pointer rounded-xl transition-colors font-oswald uppercase text-[10px] font-bold tracking-widest"
                            >
                              <span className="text-lg">🌐</span> Proxy
                            </DropdownMenuItem>
                          </>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <button
                        onClick={() => restartSession(session.name)}
                        className="px-6 py-3 bg-white/5 border border-white/10 text-gray-300 text-[10px] rounded-xl font-bold hover:bg-white/10 hover:text-white transition-all uppercase tracking-widest font-oswald"
                      >
                        Reiniciar
                      </button>
                      <button
                        onClick={() => deleteSession(session.name)}
                        className="px-6 py-3 bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] rounded-xl font-bold hover:bg-red-500/20 transition-all uppercase tracking-widest font-oswald"
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dialog BChat */}
      <Dialog open={bchatDialogOpen} onOpenChange={setBchatDialogOpen}>
        <DialogContent className="sm:max-w-[920px] z-[1100] bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 text-white rounded-[2.5rem] shadow-2xl overflow-hidden p-0 backdrop-blur-3xl">
          <div className="absolute inset-0 bg-blue-500/5 pointer-events-none" />

          <div className="p-5 relative z-10">
            <DialogHeader className="mb-4">
              <DialogTitle className="text-3xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-2">
                <div className="w-2 h-8 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></div>
                {activeSessionForAction && bchatConfigs[activeSessionForAction]
                  ? 'Integração BChat Ativa'
                  : 'Configurar BChat'}
              </DialogTitle>
              <DialogDescription className="text-gray-400 font-oswald text-[10px] font-bold uppercase tracking-[0.2em] mt-1">
                {activeSessionForAction && bchatConfigs[activeSessionForAction]
                  ? 'Esta conexão já possui uma caixa de entrada configurada no BChat.'
                  : 'Configure as opções para criar a inbox no BChat.'}
              </DialogDescription>
            </DialogHeader>

            {activeSessionForAction && bchatConfigs[activeSessionForAction] ? (
              <div className="space-y-3">
                <div className="bg-white/5 rounded-3xl border border-white/10 p-4 space-y-3 backdrop-blur-md">
                  <div className="flex justify-between items-center border-b border-white/5 pb-2">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald">Status da Integração</span>
                    <span className="inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-500/10 text-green-400 border border-green-500/20">
                      {bchatConfigs[activeSessionForAction].enabled ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest font-oswald">ID da Conta</p>
                      <p className="text-lg font-bold text-white font-oswald tracking-wider">
                        {bchatConfigs[activeSessionForAction].accountId || '-'}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest font-oswald">Nome da Caixa</p>
                      <p className="text-lg font-bold text-white font-oswald">
                        {bchatConfigs[activeSessionForAction].nameInbox || '-'}
                      </p>
                    </div>
                  </div>

                  <div className="pt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div className="p-4 bg-white/5 rounded-2xl border border-white/10 flex flex-col gap-1 items-center text-center">
                      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest font-oswald">Reabrir</span>
                      <span className={`text-xs font-bold ${bchatConfigs[activeSessionForAction].reopenConversation ? 'text-green-400' : 'text-gray-500'}`}>
                        {bchatConfigs[activeSessionForAction].reopenConversation ? 'SIM' : 'NÃO'}
                      </span>
                    </div>
                    <div className="p-4 bg-white/5 rounded-2xl border border-white/10 flex flex-col gap-1 items-center text-center">
                      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest font-oswald">Contatos</span>
                      <span className={`text-xs font-bold ${bchatConfigs[activeSessionForAction].importContacts ? 'text-green-400' : 'text-gray-500'}`}>
                        {bchatConfigs[activeSessionForAction].importContacts ? 'SIM' : 'NÃO'}
                      </span>
                    </div>
                    <div className="p-4 bg-white/5 rounded-2xl border border-white/10 flex flex-col gap-1 items-center text-center">
                      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest font-oswald">Mensagens</span>
                      <span className={`text-xs font-bold ${bchatConfigs[activeSessionForAction].importMessages ? 'text-green-400' : 'text-gray-500'}`}>
                        {bchatConfigs[activeSessionForAction].importMessages ? 'SIM' : 'NÃO'}
                      </span>
                    </div>
                  </div>

                  {(() => {
                    const session = sessions.find(s => s.name === activeSessionForAction);
                    if (session?.provider === 'WAHA') {
                      return (
                        <div className="border-t border-white/5 pt-2 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald">Agentes Vinculados</span>
                            {membersLoading && <span className="text-[10px] text-gray-500 font-oswald">Carregando...</span>}
                            {membersError && <span className="text-[10px] text-red-400 font-oswald">{membersError}</span>}
                          </div>
                          {!membersLoading && !membersError && (
                            <div className="space-y-1">
                              {selectedAgentIds.length === 0 && (
                                <p className="text-xs text-gray-500 font-oswald">Nenhum agente vinculado</p>
                              )}
                              {selectedAgentIds.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {selectedAgentIds.map(id => {
                                    const agent = availableAgents.find(a => a.id === id);
                                    return (
                                      <span key={id} className="px-3 py-1 bg-blue-500/10 text-blue-400 text-[10px] rounded-full border border-blue-500/20 font-oswald">
                                        {agent?.available_name || agent?.name || `Agente #${id}`}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                              <div className="pt-1">
                                {selectedAgentIds.length > 0 ? (
                                  <button
                                    onClick={handleSaveAgents}
                                    disabled={savingAgents}
                                    className="px-4 py-2 bg-blue-600 text-white text-[10px] rounded-xl font-bold hover:shadow-[0_0_15px_rgba(37,99,235,0.3)] transition-all uppercase tracking-[0.2em] border border-blue-500 font-oswald disabled:opacity-50"
                                  >
                                    {savingAgents ? 'Salvando...' : 'Salvar Agentes'}
                                  </button>
                                ) : (
                                  <p className="text-[10px] text-gray-500 font-oswald">Selecione agentes abaixo e clique em Salvar</p>
                                )}
                              </div>
                              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                                {agentsLoading && <p className="text-[10px] text-gray-500 font-oswald">Carregando agentes...</p>}
                                {agentsError && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-red-400 font-oswald">{agentsError}</span>
                                    <button
                                      onClick={() => {
                                        setAgentsLoading(true);
                                        setAgentsError(null);
                                        authenticatedFetch('/api/chatwoot/agents')
                                          .then(r => r.ok ? r.json() : Promise.reject(r))
                                          .then(d => { setAvailableAgents(Array.isArray(d.agents) ? d.agents : []); setAgentsLoading(false); })
                                          .catch(() => { setAgentsError('Falha ao carregar'); setAgentsLoading(false); });
                                      }}
                                      className="text-[10px] text-blue-400 hover:text-blue-300 font-oswald"
                                    >
                                      Tentar novamente
                                    </button>
                                  </div>
                                )}
                                {!agentsLoading && !agentsError && availableAgents.length === 0 && (
                                  <p className="text-[10px] text-gray-500 font-oswald">Nenhum agente encontrado na conta</p>
                                )}
                                {!agentsLoading && !agentsError && filteredAgents.map(agent => (
                                  <label key={agent.id} className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 rounded-xl cursor-pointer transition-colors">
                                    <input
                                      type="checkbox"
                                      checked={selectedAgentIds.includes(agent.id)}
                                      onChange={() => {
                                        setSelectedAgentIds(prev =>
                                          prev.includes(agent.id) ? prev.filter(id => id !== agent.id) : [...prev, agent.id]
                                        );
                                      }}
                                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500/30"
                                    />
                                    <div className="flex flex-col">
                                      <span className="text-xs text-white font-oswald">{agent.available_name || agent.name}</span>
                                      <span className="text-[10px] text-gray-500 font-oswald">{agent.email}</span>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setBchatDialogOpen(false)}
                    className="px-8 py-3 bg-white/5 text-gray-400 text-[10px] rounded-xl font-bold hover:bg-white/10 transition-all border border-white/10 uppercase tracking-[0.2em] font-oswald"
                  >
                    Fechar
                  </button>
                  <button
                    onClick={handleDisconnectBchat}
                    disabled={disconnecting}
                    className="px-8 py-3 bg-red-600 text-white text-[10px] rounded-xl font-bold hover:shadow-[0_0_20px_rgba(220,38,38,0.3)] transition-all uppercase tracking-[0.2em] border border-red-500 font-oswald disabled:opacity-50"
                  >
                    {disconnecting ? 'Desconectando...' : 'Desconectar Integração'}
                  </button>
                </div>
              </div>
            ) : (
                <div className="space-y-4">
                  <div className="flex items-end gap-2">
                    <div className="flex-shrink-0 p-1 bg-white/5 rounded-2xl border border-white/10">
                      <button
                        onClick={() => setAssociateExistingMode(false)}
                        className={`px-6 py-3 text-[10px] font-bold uppercase tracking-widest font-oswald rounded-xl transition-all whitespace-nowrap ${!associateExistingMode ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-500 hover:text-white'}`}
                      >
                        Criar Nova Caixa
                      </button>
                      <button
                        onClick={() => setAssociateExistingMode(true)}
                        className={`px-6 py-3 text-[10px] font-bold uppercase tracking-widest font-oswald rounded-xl transition-all whitespace-nowrap ${associateExistingMode ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-500 hover:text-white'}`}
                      >
                        Associar Existente
                      </button>
                    </div>

                    {associateExistingMode ? (
                      <div className="flex-1 space-y-1.5">
                        <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald ml-1">Selecionar Caixa de Entrada</Label>
                        <select
                          className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm text-white focus:ring-2 focus:ring-blue-500/30 outline-none transition-all font-oswald uppercase tracking-wider"
                          value={selectedInboxId ?? ''}
                          onChange={(e) => setSelectedInboxId(Number(e.target.value))}
                        >
                          {inboxesLoading && <option value="" disabled className="bg-[#1e293b]">Carregando caixas...</option>}
                          {!inboxesLoading && inboxesError && <option value="" disabled className="bg-[#1e293b]">{inboxesError}</option>}
                          {!inboxesLoading && !inboxesError && availableInboxes.length === 0 && <option value="" disabled className="bg-[#1e293b]">Nenhuma caixa encontrada</option>}
                          {!inboxesLoading && !inboxesError && availableInboxes.map((i) => (
                            <option key={i.id} value={i.id} className="bg-[#1e293b]">{i.name} ({i.channel_type})</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="flex-1 space-y-1.5">
                        <Label htmlFor="inboxName" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald ml-1">Nome da Nova Caixa</Label>
                        <Input
                          id="inboxName"
                          placeholder="Ex: Atendimento WhatsApp"
                          value={bchatForm.inboxName}
                          onChange={(e) => setBchatForm({ ...bchatForm, inboxName: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 h-14 text-sm text-white placeholder:text-gray-600 focus:ring-2 focus:ring-blue-500/30 outline-none transition-all font-oswald uppercase tracking-wider"
                        />
                      </div>
                    )}
                  </div>

                  {associateExistingMode ? (
                    <div className="space-y-3">

                    {(() => {
                      const session = sessions.find(s => s.name === activeSessionForAction);
                      if (session?.provider !== 'WAHA') return null;
                      return (
                        <div className="space-y-1.5 border-t border-white/5 pt-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald">Agentes da Conta</span>
                            {membersLoading && <span className="text-[10px] text-gray-500 font-oswald">Carregando...</span>}
                          </div>
                          {membersError && <span className="text-[10px] text-red-400 font-oswald">{membersError}</span>}
                          <Input
                            placeholder="Buscar agente por nome ou email..."
                            value={agentSearch}
                            onChange={(e) => setAgentSearch(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl p-3 text-sm text-white placeholder:text-gray-600 focus:ring-2 focus:ring-blue-500/30 outline-none transition-all font-oswald"
                          />
                          <div className="space-y-0.5 max-h-48 overflow-y-auto">
                            {agentsLoading && <p className="text-[10px] text-gray-500 font-oswald p-2">Carregando agentes...</p>}
                            {agentsError && (
                              <div className="flex items-center gap-2 p-2">
                                <span className="text-[10px] text-red-400 font-oswald">{agentsError}</span>
                                <button
                                  onClick={() => {
                                    setAgentsLoading(true);
                                    setAgentsError(null);
                                    authenticatedFetch('/api/chatwoot/agents')
                                      .then(r => r.ok ? r.json() : Promise.reject(r))
                                      .then(d => { setAvailableAgents(Array.isArray(d.agents) ? d.agents : []); setAgentsLoading(false); })
                                      .catch(() => { setAgentsError('Falha'); setAgentsLoading(false); });
                                  }}
                                  className="text-[10px] text-blue-400 hover:text-blue-300 font-oswald"
                                >
                                  Tentar novamente
                                </button>
                              </div>
                            )}
                            {!agentsLoading && !agentsError && filteredAgents.length === 0 && (
                              <p className="text-[10px] text-gray-500 font-oswald p-2">Nenhum agente encontrado</p>
                            )}
                            {!agentsLoading && !agentsError && filteredAgents.map(agent => (
                              <label key={agent.id} className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 rounded-xl cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  checked={selectedAgentIds.includes(agent.id)}
                                  onChange={() => {
                                    setSelectedAgentIds(prev =>
                                      prev.includes(agent.id) ? prev.filter(id => id !== agent.id) : [...prev, agent.id]
                                    );
                                  }}
                                  className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500/30"
                                />
                                <div className="flex flex-col">
                                  <span className="text-xs text-white font-oswald">{agent.available_name || agent.name}</span>
                                  <span className="text-[10px] text-gray-500 font-oswald">{agent.email}</span>
                                </div>
                              </label>
                            ))}
                          </div>
                          {selectedAgentIds.length > 0 && (
                            <p className="text-[10px] text-gray-500 font-oswald">
                              Agentes desmarcados serão removidos da caixa ao salvar.
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    <div className="grid grid-cols-4 gap-1.5">
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/10">
                        <Label htmlFor="pending" className="text-[10px] font-bold text-gray-300 uppercase tracking-widest font-oswald cursor-pointer">Pendente</Label>
                        <Toggle
                          id="pending"
                          pressed={bchatForm.pending}
                          onPressedChange={(val) => setBchatForm({ ...bchatForm, pending: val })}
                          className={`rounded-full px-4 h-8 text-[10px] font-bold transition-all ${bchatForm.pending ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-500'}`}
                        >
                          {bchatForm.pending ? 'SIM' : 'NÃO'}
                        </Toggle>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/10">
                        <Label htmlFor="reopen" className="text-[10px] font-bold text-gray-300 uppercase tracking-widest font-oswald cursor-pointer">Reabrir</Label>
                        <Toggle
                          id="reopen"
                          pressed={bchatForm.reopen}
                          onPressedChange={(val) => setBchatForm({ ...bchatForm, reopen: val })}
                          className={`rounded-full px-4 h-8 text-[10px] font-bold transition-all ${bchatForm.reopen ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-500'}`}
                        >
                          {bchatForm.reopen ? 'SIM' : 'NÃO'}
                        </Toggle>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/10">
                        <Label htmlFor="importContacts" className="text-[10px] font-bold text-gray-300 uppercase tracking-widest font-oswald cursor-pointer">Contatos</Label>
                        <Toggle
                          id="importContacts"
                          pressed={bchatForm.importContacts}
                          onPressedChange={(val) => setBchatForm({ ...bchatForm, importContacts: val })}
                          className={`rounded-full px-4 h-8 text-[10px] font-bold transition-all ${bchatForm.importContacts ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-500'}`}
                        >
                          {bchatForm.importContacts ? 'SIM' : 'NÃO'}
                        </Toggle>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/10">
                        <Label htmlFor="importMessages" className="text-[10px] font-bold text-gray-300 uppercase tracking-widest font-oswald cursor-pointer">Mensagens</Label>
                        <Toggle
                          id="importMessages"
                          pressed={bchatForm.importMessages}
                          onPressedChange={(val) => setBchatForm({ ...bchatForm, importMessages: val })}
                          className={`rounded-full px-4 h-8 text-[10px] font-bold transition-all ${bchatForm.importMessages ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-500'}`}
                        >
                          {bchatForm.importMessages ? 'SIM' : 'NÃO'}
                        </Toggle>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => setBchatDialogOpen(false)}
                        className="px-8 py-3 bg-white/5 text-gray-400 text-[10px] rounded-xl font-bold hover:bg-white/10 transition-all border border-white/10 uppercase tracking-[0.2em] font-oswald"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleAssociateExistingInbox}
                        disabled={associating}
                        className="px-10 py-3 bg-blue-600 text-white text-[10px] rounded-xl font-bold hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all uppercase tracking-[0.2em] border border-blue-500 font-oswald disabled:opacity-50 relative overflow-hidden group/btn"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                        {associating ? 'Associando...' : 'Confirmar Associação'}
                      </button>
                    </div>
                  </div>
                ) : (
                    <div className="space-y-3">

                    <div className="grid grid-cols-4 gap-1.5">
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/10">
                        <Label htmlFor="pending-new" className="text-[10px] font-bold text-gray-300 uppercase tracking-widest font-oswald cursor-pointer">Pendente</Label>
                        <Toggle
                          id="pending-new"
                          pressed={bchatForm.pending}
                          onPressedChange={(val) => setBchatForm({ ...bchatForm, pending: val })}
                          className={`rounded-full px-4 h-8 text-[10px] font-bold transition-all ${bchatForm.pending ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-500'}`}
                        >
                          {bchatForm.pending ? 'SIM' : 'NÃO'}
                        </Toggle>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/10">
                        <Label htmlFor="reopen-new" className="text-[10px] font-bold text-gray-300 uppercase tracking-widest font-oswald cursor-pointer">Reabrir</Label>
                        <Toggle
                          id="reopen-new"
                          pressed={bchatForm.reopen}
                          onPressedChange={(val) => setBchatForm({ ...bchatForm, reopen: val })}
                          className={`rounded-full px-4 h-8 text-[10px] font-bold transition-all ${bchatForm.reopen ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-500'}`}
                        >
                          {bchatForm.reopen ? 'SIM' : 'NÃO'}
                        </Toggle>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/10">
                        <Label htmlFor="importContacts-new" className="text-[10px] font-bold text-gray-300 uppercase tracking-widest font-oswald cursor-pointer">Contatos</Label>
                        <Toggle
                          id="importContacts-new"
                          pressed={bchatForm.importContacts}
                          onPressedChange={(val) => setBchatForm({ ...bchatForm, importContacts: val })}
                          className={`rounded-full px-4 h-8 text-[10px] font-bold transition-all ${bchatForm.importContacts ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-500'}`}
                        >
                          {bchatForm.importContacts ? 'SIM' : 'NÃO'}
                        </Toggle>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/10">
                        <Label htmlFor="importMessages-new" className="text-[10px] font-bold text-gray-300 uppercase tracking-widest font-oswald cursor-pointer">Mensagens</Label>
                        <Toggle
                          id="importMessages-new"
                          pressed={bchatForm.importMessages}
                          onPressedChange={(val) => setBchatForm({ ...bchatForm, importMessages: val })}
                          className={`rounded-full px-4 h-8 text-[10px] font-bold transition-all ${bchatForm.importMessages ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-500'}`}
                        >
                          {bchatForm.importMessages ? 'SIM' : 'NÃO'}
                        </Toggle>
                      </div>
                    </div>

                    {(() => {
                      const session = sessions.find(s => s.name === activeSessionForAction);
                      if (session?.provider !== 'WAHA') return null;
                      return (
                        <div className="space-y-1.5 border-t border-white/5 pt-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald">Agentes da Conta</span>
                            {agentsLoading && <span className="text-[10px] text-gray-500 font-oswald">Carregando...</span>}
                          </div>
                          {agentsError && (
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-red-400 font-oswald">{agentsError}</span>
                              <button
                                onClick={() => {
                                  setAgentsLoading(true);
                                  setAgentsError(null);
                                  authenticatedFetch('/api/chatwoot/agents')
                                    .then(r => r.ok ? r.json() : Promise.reject(r))
                                    .then(d => { setAvailableAgents(Array.isArray(d.agents) ? d.agents : []); setAgentsLoading(false); })
                                    .catch(() => { setAgentsError('Falha'); setAgentsLoading(false); });
                                }}
                                className="text-[10px] text-blue-400 hover:text-blue-300 font-oswald"
                              >
                                Tentar novamente
                              </button>
                            </div>
                          )}
                          <Input
                            placeholder="Buscar agente por nome ou email..."
                            value={agentSearch}
                            onChange={(e) => setAgentSearch(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl p-3 text-sm text-white placeholder:text-gray-600 focus:ring-2 focus:ring-blue-500/30 outline-none transition-all font-oswald"
                          />
                          <div className="space-y-0.5 max-h-48 overflow-y-auto">
                            {agentsLoading && <p className="text-[10px] text-gray-500 font-oswald p-2">Carregando agentes...</p>}
                            {!agentsLoading && !agentsError && filteredAgents.length === 0 && (
                              <p className="text-[10px] text-gray-500 font-oswald p-2">Nenhum agente encontrado na conta</p>
                            )}
                            {!agentsLoading && !agentsError && filteredAgents.map(agent => (
                              <label key={agent.id} className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 rounded-xl cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  checked={selectedAgentIds.includes(agent.id)}
                                  onChange={() => {
                                    setSelectedAgentIds(prev =>
                                      prev.includes(agent.id) ? prev.filter(id => id !== agent.id) : [...prev, agent.id]
                                    );
                                  }}
                                  className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500/30"
                                />
                                <div className="flex flex-col">
                                  <span className="text-xs text-white font-oswald">{agent.available_name || agent.name}</span>
                                  <span className="text-[10px] text-gray-500 font-oswald">{agent.email}</span>
                                </div>
                              </label>
                            ))}
                          </div>
                          {selectedAgentIds.length === 0 && (
                            <p className="text-[10px] text-gray-500 font-oswald">Nenhum agente selecionado. A caixa será criada sem agentes extras.</p>
                          )}
                        </div>
                      );
                    })()}

                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => setBchatDialogOpen(false)}
                        className="px-8 py-3 bg-white/5 text-gray-400 text-[10px] rounded-xl font-bold hover:bg-white/10 transition-all border border-white/10 uppercase tracking-[0.2em] font-oswald"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleCreateBChatInbox}
                        disabled={isCreating}
                        className="px-10 py-3 bg-green-600 text-white text-[10px] rounded-xl font-bold hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] transition-all uppercase tracking-[0.2em] border border-green-500 font-oswald relative overflow-hidden group/btn disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                        {isCreating ? 'Criando...' : 'Criar Integração'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Criar Conexão */}
      {createSessionModalOpen && (
        <Portal>
          <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center z-[9999]">
            <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-[2.5rem] shadow-2xl p-10 w-full max-w-md border border-white/10 m-4 relative animate-in fade-in zoom-in duration-300 overflow-hidden">
              <div className="absolute inset-0 bg-blue-500/5 pointer-events-none" />

              <button
                onClick={() => setCreateSessionModalOpen(false)}
                className="absolute top-6 right-6 p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-all z-20"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>

              <div className="relative z-10">
                <div className="text-center mb-10">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-[2rem] mx-auto mb-6 flex items-center justify-center shadow-[0_0_30px_rgba(37,99,235,0.3)] transform rotate-3 hover:rotate-6 transition-transform">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h2 id="create-session-title" className="text-3xl font-bold text-white font-oswald uppercase tracking-tight">
                    NOVA CONEXÃO
                  </h2>
                  <p className="text-gray-400 mt-3 text-[10px] font-bold uppercase tracking-widest font-oswald">
                    Conecte uma nova conexão do WhatsApp
                  </p>
                </div>

                <div className="space-y-8">
                  <div>
                    <label htmlFor="session-provider" className="block text-[10px] font-bold text-gray-400 mb-3 px-1 font-oswald uppercase tracking-widest">
                      PROVEDOR
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => !isCreating && setProviderDropdownOpen(!providerDropdownOpen)}
                        className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:ring-2 focus:ring-blue-500/30 text-sm text-left flex items-center justify-between transition-all hover:bg-white/10 text-white font-oswald uppercase tracking-wider h-14"
                        disabled={isCreating}
                      >
                        <span className="flex items-center gap-3">
                          <img
                            src={getProviderMeta(newSessionProvider).icon}
                            alt={newSessionProvider}
                            className="w-6 h-6 object-contain"
                          />
                          <span className="font-bold">
                            {getProviderMeta(newSessionProvider).label}
                          </span>
                        </span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${providerDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {providerDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setProviderDropdownOpen(false)}
                          />
                          <div className="absolute z-20 w-full mt-2 bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 p-2">
                            {allowedProviders.includes('WAHA') && (
                              <button
                                type="button"
                                onClick={() => {
                                  setNewSessionProvider('WAHA');
                                  setProviderDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-blue-600/20 hover:text-blue-400 rounded-xl transition-all font-oswald uppercase text-[10px] font-bold tracking-widest ${newSessionProvider === 'WAHA' ? 'bg-blue-600/20 text-blue-400' : 'text-white'}`}
                              >
                                <img src="/iconewaha.png" alt="Waha" className="w-5 h-5 object-contain" />
                                <span>Waha</span>
                              </button>
                            )}
                            {allowedProviders.includes('EVOLUTION') && (
                              <button
                                type="button"
                                onClick={() => {
                                  setNewSessionProvider('EVOLUTION');
                                  setProviderDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-blue-600/20 hover:text-blue-400 rounded-xl transition-all font-oswald uppercase text-[10px] font-bold tracking-widest ${newSessionProvider === 'EVOLUTION' ? 'bg-blue-600/20 text-blue-400' : 'text-white'}`}
                              >
                                <img src="/iconeevolutionapi.png" alt="Evolution API" className="w-5 h-5 object-contain" />
                                <span>Evolution API</span>
                              </button>
                            )}
                            {allowedProviders.includes('QUEPASA') && (
                              <button
                                type="button"
                                onClick={() => {
                                  setNewSessionProvider('QUEPASA');
                                  setProviderDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-blue-600/20 hover:text-blue-400 rounded-xl transition-all font-oswald uppercase text-[10px] font-bold tracking-widest ${newSessionProvider === 'QUEPASA' ? 'bg-blue-600/20 text-blue-400' : 'text-white'}`}
                              >
                                <img src="/iconequepasa.png" alt="Quepasa" className="w-5 h-5 object-contain" />
                                <span>Quepasa</span>
                              </button>
                            )}
                            {allowedProviders.includes('EVOGO') && (
                              <button
                                type="button"
                                onClick={() => {
                                  setNewSessionProvider('EVOGO');
                                  setProviderDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-blue-600/20 hover:text-blue-400 rounded-xl transition-all font-oswald uppercase text-[10px] font-bold tracking-widest ${newSessionProvider === 'EVOGO' ? 'bg-blue-600/20 text-blue-400' : 'text-white'}`}
                              >
                                <img src="/iconeevogo.svg" alt="Evogo" className="w-5 h-5 object-contain" />
                                <span>Evogo</span>
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="session-name" className="block text-[10px] font-bold text-gray-400 mb-3 px-1 font-oswald uppercase tracking-widest">
                      NOME DA CONEXÃO
                    </label>
                    <input
                      id="session-name"
                      type="text"
                      value={newSessionName}
                      onChange={(e) => setNewSessionName(e.target.value)}
                      placeholder="Ex: vendas, suporte"
                      className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:ring-2 focus:ring-blue-500/30 text-sm outline-none transition-all placeholder:text-gray-600 text-white font-oswald uppercase tracking-wider h-14"
                      disabled={isCreating}
                      autoFocus
                    />
                  </div>

                  <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <label htmlFor="interactive-campaign-toggle" className="text-[10px] font-bold text-white cursor-pointer font-oswald uppercase tracking-widest">
                            CAMPANHA INTERATIVA
                          </label>
                        </div>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest font-oswald">
                          Habilita respostas automáticas
                        </p>
                      </div>
                      <Toggle
                        id="interactive-campaign-toggle"
                        pressed={interactiveCampaignEnabled}
                        onPressedChange={(val) => setInteractiveCampaignEnabled(val)}
                        className={`rounded-full px-4 h-8 text-[10px] font-bold transition-all ${interactiveCampaignEnabled ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-500'}`}
                        disabled={isCreating}
                      >
                        {interactiveCampaignEnabled ? 'SIM' : 'NÃO'}
                      </Toggle>
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button
                      onClick={() => {
                        setCreateSessionModalOpen(false);
                        setNewSessionName('');
                        setNewSessionProvider(allowedProviders[0] || 'WAHA');
                        setInteractiveCampaignEnabled(false);
                      }}
                      className="flex-1 px-6 py-4 bg-white/5 border border-white/10 text-gray-400 text-[10px] rounded-xl font-bold hover:bg-white/10 hover:text-white transition-all uppercase tracking-widest font-oswald"
                      disabled={isCreating}
                    >
                      Cancelar
                    </button>

                    <button
                      type="button"
                      onClick={async () => {
                        await createSession();
                        setCreateSessionModalOpen(false);
                      }}
                      disabled={isCreating || !newSessionName.trim()}
                      className="flex-1 px-6 py-4 bg-blue-600 text-white text-[10px] rounded-xl font-bold hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all uppercase tracking-widest border border-blue-500 font-oswald relative overflow-hidden group/btn disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                      {isCreating ? 'Criando...' : 'Criar Conexão'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Modal do QR Code */}
      {qrModalOpen && currentQRSession && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center z-[9999]" onClick={closeQRModal}>
          <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-[2.5rem] shadow-2xl w-full max-w-md border border-white/10 m-4 relative animate-in fade-in zoom-in duration-300 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="absolute inset-0 bg-blue-500/5 pointer-events-none" />

            <div className="relative z-10">
              <div className="flex justify-between items-center p-8 border-b border-white/10">
                <h3 className="text-2xl font-bold text-white font-oswald uppercase tracking-tight">
                  QR CODE - {currentQRSession.displayName || currentQRSession.name}
                </h3>
                <button
                  onClick={closeQRModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-8">
                {currentQRSession?.qr ? (
                  <div className="text-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald mb-8">
                      Escaneie o QR Code com seu WhatsApp
                    </p>
                    <div className="bg-white p-6 rounded-[2rem] inline-block shadow-[0_0_50px_rgba(255,255,255,0.1)] mb-8">
                      {(() => {
                        // Para Evolution: QR vem em base64 direto
                        if (currentQRSession.provider === 'EVOLUTION' && currentQRSession.qr?.startsWith('data:image')) {
                          return (
                            <img
                              src={currentQRSession.qr}
                              alt="QR Code WhatsApp"
                              className="w-64 h-64 mx-auto block rounded-xl"
                            />
                          );
                        }

                        // Para Quepasa: QR vem em base64
                        if (currentQRSession.provider === 'QUEPASA' && currentQRSession.qr?.startsWith('data:image')) {
                          return (
                            <img
                              src={currentQRSession.qr}
                              alt="QR Code WhatsApp"
                              className="w-64 h-64 mx-auto block rounded-xl"
                              onError={() => {
                                console.error('Erro ao carregar QR Quepasa base64');
                              }}
                            />
                          );
                        }

                        // Para WAHA e Evogo: QR já vem processado pelo backend em base64
                        if ((currentQRSession.provider === 'WAHA' || currentQRSession.provider === 'EVOGO') && currentQRSession.qr?.startsWith('data:image')) {
                          return (
                            <img
                              src={currentQRSession.qr}
                              alt="QR Code WhatsApp"
                              className="w-64 h-64 mx-auto block rounded-xl"
                              onError={() => {
                                console.error('Erro ao carregar QR base64');
                              }}
                            />
                          );
                        }

                        // Fallback: detectar automaticamente
                        const qrSrc = currentQRSession.qr?.startsWith('data:image')
                          ? currentQRSession.qr
                          : `/api/waha/sessions/${currentQRSession.name}/auth/qr`;

                        return (
                          <img
                            src={qrSrc}
                            alt="QR Code WhatsApp"
                            className="w-64 h-64 mx-auto block rounded-xl"
                            onError={() => {
                              console.error('Erro ao carregar QR fallback');
                            }}
                          />
                        );
                      })()}
                    </div>
                    {currentQRSession.qrExpiresAt && (
                      <div className="mt-4">
                        <QRCountdown expiresAt={currentQRSession.qrExpiresAt} />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-6 shadow-[0_0_20px_rgba(37,99,235,0.2)]"></div>
                    <span className="text-[10px] font-bold text-white uppercase tracking-widest font-oswald">Gerando QR Code...</span>
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest font-oswald mt-2">Aguarde um momento</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-4 p-8 border-t border-white/10">
                <button
                  onClick={() => openQRModal(currentQRSession.name)}
                  className="px-6 py-3 bg-white/5 border border-white/10 text-gray-400 text-[10px] rounded-xl font-bold hover:bg-white/10 hover:text-white transition-all uppercase tracking-widest font-oswald"
                >
                  Atualizar QR
                </button>
                <button
                  onClick={closeQRModal}
                  className="px-8 py-3 bg-blue-600 text-white text-[10px] rounded-xl font-bold hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all uppercase tracking-widest border border-blue-500 font-oswald relative overflow-hidden group/btn"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Dialog Proxy */}
      <Dialog open={proxyDialogOpen} onOpenChange={setProxyDialogOpen}>
        <DialogContent className="z-[120] sm:max-w-[850px] sm:top-[52%] bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 shadow-2xl rounded-[2.5rem] overflow-hidden p-0 text-white backdrop-blur-3xl">
          <div className="bg-white/5 border-b border-white/10 px-6 py-4 flex items-center justify-between">
            <DialogHeader className="p-0 space-y-0">
              <DialogTitle className="text-white text-2xl font-bold flex items-center gap-3 font-oswald tracking-wider uppercase">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center border border-blue-500/30">
                  <Globe size={20} className="text-blue-400" />
                </div>
                CONFIGURAR PROXY
              </DialogTitle>
              <DialogDescription className="text-gray-400 text-[10px] font-bold uppercase tracking-widest font-oswald mt-1">
                {activeSessionForAction && `Ajuste as configurações para a conexão ${activeSessionForAction.split('_')[0]}`}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-6 space-y-2 max-h-[calc(100vh-7rem)] overflow-y-auto custom-scrollbar">
            {loadingProxy ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin shadow-[0_0_20px_rgba(37,99,235,0.2)]"></div>
                <p className="text-[10px] font-bold text-white uppercase tracking-widest font-oswald">Buscando configurações...</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  <div className="p-4 bg-white/5 rounded-2xl border border-white/10 group hover:bg-white/10 transition-all duration-300 flex flex-col justify-between min-h-[112px]">
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold text-white uppercase tracking-widest font-oswald">Ativar Proxy</Label>
                    </div>
                    <div className="pt-3">
                      <Toggle
                        pressed={proxyForm.enabled}
                        onPressedChange={(val) => setProxyForm({ ...proxyForm, enabled: val })}
                        className={`rounded-xl px-6 h-10 transition-all duration-300 text-[10px] font-bold uppercase tracking-widest font-oswald ${proxyForm.enabled ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]' : 'bg-white/5 text-gray-500 border border-white/10'}`}
                      >
                        {proxyForm.enabled ? 'ATIVADO' : 'DESATIVADO'}
                      </Toggle>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-500/10 rounded-2xl border border-blue-500/20 space-y-2 min-h-[112px] flex flex-col justify-between">
                    <p className="text-[10px] text-blue-200 font-bold uppercase tracking-widest font-oswald">
                      Proxy automático Webshare
                    </p>
                    <button
                      onClick={handleApplyAutomaticWebshareProxy}
                      disabled={applyingAutoWebshare || !['EVOGO', 'WAHA'].includes(sessions.find((s) => s.name === activeSessionForAction)?.provider || '')}
                      className="px-6 py-3 bg-blue-600 text-white text-[10px] rounded-xl font-bold hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all uppercase tracking-widest border border-blue-500 font-oswald relative overflow-hidden group/btn disabled:opacity-50"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                      {applyingAutoWebshare ? 'Aplicando...' : 'Aplicar Webshare Automaticamente'}
                    </button>
                  </div>

                  <div className="p-4 bg-white/5 rounded-2xl border border-white/10 space-y-2 min-h-[112px]">
                    <p className="text-[10px] text-gray-300 font-bold uppercase tracking-widest font-oswald">
                      Diagnóstico de IP externo
                    </p>
                    <button
                      onClick={handleCheckProxyIp}
                      disabled={checkingProxyIp}
                      className="px-6 py-3 bg-white/10 text-white text-[10px] rounded-xl font-bold hover:bg-white/20 transition-all uppercase tracking-widest border border-white/10 font-oswald disabled:opacity-50"
                    >
                      {checkingProxyIp ? 'Consultando IP...' : 'Mostrar IP Conectado'}
                    </button>
                  </div>
                </div>

                <div className="p-4 bg-white/5 rounded-2xl border border-white/10 space-y-2">
                  {proxyExternalIp && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div className="p-3 rounded-xl border border-green-400/20 bg-green-500/10">
                        <p className="text-[10px] text-green-200 uppercase tracking-wider font-oswald">IP atual</p>
                        <p className="text-[12px] text-green-300 font-mono break-all">{proxyExternalIp}</p>
                      </div>
                      <div className="p-3 rounded-xl border border-white/10 bg-white/5">
                        <p className="text-[10px] text-gray-300 uppercase tracking-wider font-oswald">Localização</p>
                        <p className="text-[12px] text-white break-words">
                          {[
                            proxyGeoInfo.city,
                            proxyGeoInfo.region,
                            proxyGeoInfo.country
                          ].filter(Boolean).join(', ') || '-'}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1">{proxyGeoInfo.countryCode || ''}</p>
                      </div>
                      <div className="p-3 rounded-xl border border-white/10 bg-white/5">
                        <p className="text-[10px] text-gray-300 uppercase tracking-wider font-oswald">Rede</p>
                        <p className="text-[11px] text-white break-words">{proxyGeoInfo.isp || proxyGeoInfo.org || '-'}</p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {typeof proxyGeoInfo.latitude === 'number' && typeof proxyGeoInfo.longitude === 'number'
                            ? `${proxyGeoInfo.latitude.toFixed(4)}, ${proxyGeoInfo.longitude.toFixed(4)}`
                            : ''}
                        </p>
                      </div>
                    </div>
                  )}
                  {proxyIpError && (
                    <p className="text-[11px] text-red-300 break-words">
                      {proxyIpError}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 border-t border-white/10 pt-2">
                  <div className="p-4 bg-white/5 rounded-2xl border border-white/10 space-y-3">
                    <p className="text-[10px] text-gray-300 font-bold uppercase tracking-widest font-oswald">Endpoint</p>
                    <div className="space-y-2">
                      <Label htmlFor="proxyHost" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald ml-1">Host/IP</Label>
                      <Input
                        id="proxyHost"
                        placeholder="Ex: p.webshare.io"
                        className="h-12 rounded-xl bg-white/5 border-white/10 focus:bg-white/10 focus:border-blue-500/50 transition-all text-sm text-white placeholder:text-gray-600 font-medium"
                        value={proxyForm.host}
                        onChange={(e) => setProxyForm({ ...proxyForm, host: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="proxyPort" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald ml-1">Porta</Label>
                      <Input
                        id="proxyPort"
                        placeholder="80"
                        className="h-12 rounded-xl bg-white/5 border-white/10 focus:bg-white/10 focus:border-blue-500/50 transition-all text-sm text-white placeholder:text-gray-600 font-medium"
                        value={proxyForm.port}
                        onChange={(e) => setProxyForm({ ...proxyForm, port: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="p-4 bg-white/5 rounded-2xl border border-white/10 space-y-3">
                    <p className="text-[10px] text-gray-300 font-bold uppercase tracking-widest font-oswald">Protocolo</p>
                    <div className="space-y-2">
                      <Label htmlFor="proxyProtocol" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald ml-1">Tipo</Label>
                      <select
                        id="proxyProtocol"
                        className="w-full h-12 rounded-xl bg-white/5 border border-white/10 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all text-sm text-white cursor-pointer font-medium appearance-none"
                        value={proxyForm.protocol}
                        onChange={(e) => setProxyForm({ ...proxyForm, protocol: e.target.value })}
                      >
                        <option value="http" className="bg-[#1e293b]">HTTP</option>
                        <option value="https" className="bg-[#1e293b]">HTTPS</option>
                      </select>
                    </div>
                    <div className="p-3 rounded-xl border border-blue-500/20 bg-blue-500/10">
                      <p className="text-[10px] text-blue-200">
                        Para Webshare Rotating Residential, use `backbone` e país `BR` no automático.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-white/5 rounded-2xl border border-white/10 space-y-3">
                    <p className="text-[10px] text-gray-300 font-bold uppercase tracking-widest font-oswald">Autenticação</p>
                    <div className="space-y-2">
                      <Label htmlFor="proxyUser" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald ml-1">Usuário (Opcional)</Label>
                      <Input
                        id="proxyUser"
                        placeholder="user"
                        className="h-12 rounded-xl bg-white/5 border-white/10 focus:bg-white/10 focus:border-blue-500/50 transition-all text-sm font-mono text-white placeholder:text-gray-600"
                        value={proxyForm.username}
                        onChange={(e) => setProxyForm({ ...proxyForm, username: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="proxyPass" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald ml-1">Senha (Opcional)</Label>
                      <Input
                        id="proxyPass"
                        type="password"
                        placeholder="••••"
                        className="h-12 rounded-xl bg-white/5 border-white/10 focus:bg-white/10 focus:border-blue-500/50 transition-all text-sm font-mono text-white placeholder:text-gray-600"
                        value={proxyForm.password}
                        onChange={(e) => setProxyForm({ ...proxyForm, password: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2 border-t border-white/10">
                  <button
                    onClick={() => setProxyDialogOpen(false)}
                    className="px-6 py-3 bg-white/5 border border-white/10 text-gray-400 text-[10px] rounded-xl font-bold hover:bg-white/10 hover:text-white transition-all uppercase tracking-widest font-oswald"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveProxy}
                    className="px-8 py-3 bg-blue-600 text-white text-[10px] rounded-xl font-bold hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all uppercase tracking-widest border border-blue-500 font-oswald relative overflow-hidden group/btn"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                    Salvar Alterações
                  </button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Session Settings */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 shadow-2xl rounded-[2.5rem] overflow-hidden p-0 text-white backdrop-blur-3xl">
          <div className="bg-white/5 border-b border-white/10 px-8 py-6 flex items-center justify-between">
            <DialogHeader className="p-0 space-y-0">
              <DialogTitle className="text-white text-2xl font-bold flex items-center gap-3 font-oswald tracking-wider uppercase">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center border border-blue-500/30">
                  <Settings size={20} className="text-blue-400" />
                </div>
                CONFIGURAÇÕES DA INSTÂNCIA
              </DialogTitle>
              <DialogDescription className="text-gray-400 text-[10px] font-bold uppercase tracking-widest font-oswald mt-1">
                {activeSessionForAction && `Ajuste as preferências para a conexão ${activeSessionForAction.split('_')[0]}`}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
            {loadingSettings ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin shadow-[0_0_20px_rgba(37,99,235,0.2)]"></div>
                <p className="text-[10px] font-bold text-white uppercase tracking-widest font-oswald">Buscando configurações...</p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {/* Rejeitar Chamadas */}
                  <div className="flex flex-col space-y-4 p-5 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold text-white uppercase tracking-widest font-oswald">Rejeitar Chamadas</Label>
                        <p className="text-[10px] text-gray-500 font-medium">Rejeitar todas as chamadas recebidas</p>
                      </div>
                      <Toggle
                        pressed={settingsForm.rejectCall}
                        onPressedChange={(val) => setSettingsForm({ ...settingsForm, rejectCall: val })}
                        className={`rounded-xl px-4 h-9 transition-all duration-300 text-[10px] font-bold uppercase tracking-widest font-oswald ${settingsForm.rejectCall ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]' : 'bg-white/5 text-gray-500 border border-white/10'}`}
                      >
                        {settingsForm.rejectCall ? 'ON' : 'OFF'}
                      </Toggle>
                    </div>

                    {settingsForm.rejectCall && (
                      <div className="pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                        <textarea
                          className="w-full min-h-[100px] p-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all resize-none placeholder:text-gray-600 font-medium"
                          placeholder="Enviar mensagem de rejeição de chamada"
                          value={settingsForm.msgCall}
                          onChange={(e) => setSettingsForm({ ...settingsForm, msgCall: e.target.value })}
                        ></textarea>
                      </div>
                    )}
                  </div>

                  {/* Toggles Simples */}
                  {[
                    { id: 'groupsIgnore', label: 'Ignorar Grupos', desc: 'Ignorar todas as mensagens de grupos' },
                    { id: 'alwaysOnline', label: 'Sempre Online', desc: 'Permanecer sempre online' },
                    { id: 'readMessages', label: 'Visualizar Mensagens', desc: 'Marcar todas as mensagens como lidas' },
                    { id: 'syncFullHistory', label: 'Sincronizar Histórico', desc: 'Sincronizar histórico ao ler QR Code' },
                    { id: 'readStatus', label: 'Visualizar Status', desc: 'Marcar status como visualizados' },
                  ].map((item) => {
                    const id = item.id as keyof SettingsForm;
                    return (
                      <div key={id} className="flex items-center justify-between p-5 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                        <div className="space-y-1">
                          <Label className="text-[10px] font-bold text-white uppercase tracking-widest font-oswald">{item.label}</Label>
                          <p className="text-[10px] text-gray-500 font-medium">{item.desc}</p>
                        </div>
                        <Toggle
                          pressed={settingsForm[id] as boolean}
                          onPressedChange={(val) => setSettingsForm({ ...settingsForm, [id]: val })}
                          className={`rounded-xl px-4 h-9 transition-all duration-300 text-[10px] font-bold uppercase tracking-widest font-oswald ${settingsForm[id] ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]' : 'bg-white/5 text-gray-500 border border-white/10'}`}
                        >
                          {settingsForm[id] ? 'ON' : 'OFF'}
                        </Toggle>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-end gap-4 pt-6 border-t border-white/10">
                  <button
                    onClick={() => setSettingsDialogOpen(false)}
                    className="px-6 py-3 bg-white/5 border border-white/10 text-gray-400 text-[10px] rounded-xl font-bold hover:bg-white/10 hover:text-white transition-all uppercase tracking-widest font-oswald"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveSettings}
                    className="px-8 py-3 bg-blue-600 text-white text-[10px] rounded-xl font-bold hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all uppercase tracking-widest border border-blue-500 font-oswald relative overflow-hidden group/btn"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                    Salvar Alterações
                  </button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmação */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="sm:max-w-[400px] bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 shadow-2xl rounded-[2.5rem] overflow-hidden p-0 text-white backdrop-blur-3xl">
          <div className="bg-white/5 border-b border-white/10 px-8 py-6">
            <DialogHeader className="p-0 space-y-0 text-left">
              <DialogTitle className="text-white text-2xl font-bold flex items-center gap-3 font-oswald tracking-wider uppercase">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${confirmDialogConfig.type === 'danger' ? 'bg-red-500/20 border-red-500/30' : 'bg-blue-500/20 border-blue-500/30'}`}>
                  <span className="text-xl">{confirmDialogConfig.type === 'danger' ? '⚠️' : 'ℹ️'}</span>
                </div>
                {confirmDialogConfig.title}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {confirmDialogConfig.message}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-8 space-y-8">
            <p className="text-gray-300 font-oswald uppercase tracking-widest text-xs leading-relaxed">
              {confirmDialogConfig.message}
            </p>

            <div className="flex justify-end gap-4">
              <button
                onClick={() => setConfirmDialogOpen(false)}
                className="px-6 py-3 bg-white/5 border border-white/10 text-gray-400 text-[10px] rounded-xl font-bold hover:bg-white/10 hover:text-white transition-all uppercase tracking-widest font-oswald"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  confirmDialogConfig.onConfirm();
                  setConfirmDialogOpen(false);
                }}
                className={`px-8 py-3 text-white text-[10px] rounded-xl font-bold transition-all uppercase tracking-widest border font-oswald relative overflow-hidden group/btn ${confirmDialogConfig.type === 'danger' ? 'bg-red-600 border-red-500 hover:shadow-[0_0_20px_rgba(239,68,68,0.3)]' : 'bg-blue-600 border-blue-500 hover:shadow-[0_0_20px_rgba(37,99,235,0.3)]'}`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                {confirmDialogConfig.confirmText}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
